/**
 * @module DataContext
 * @description Contexto global que centraliza toda la gestión de datos del sistema ISO 27001/27002.
 *
 * Este es el contexto más grande del sistema y actúa como capa de datos principal entre
 * los componentes React y Supabase/PostgreSQL. Administra controles ISO, referentes técnicos,
 * tareas, evidencias, notificaciones y el historial de auditoría.
 *
 * ## Entidades gestionadas
 * | Entidad              | Tabla PostgreSQL       | Estado React             |
 * |----------------------|------------------------|--------------------------|
 * | Controles ISO        | `iso_controls`         | `controls`               |
 * | Referentes técnicos  | `technical_referents`  | `technicalReferents`     |
 * | Notificaciones       | `notifications`        | `notifications`          |
 * | Emails (cola)        | `email_queue`          | `emailNotifications`     |
 * | Tareas               | `tasks`                | anidado en controles     |
 * | Evidencias           | `evidence`             | anidado en controles     |
 * | Audit trail          | `audit_trail`          | anidado en controles     |
 *
 * ## Patrón de actualización optimista
 * Todas las mutaciones siguen el patrón:
 * 1. Actualizar estado React local inmediatamente (mejor UX, sin latencia percibida)
 * 2. Persistir en Supabase/PostgreSQL de forma asíncrona
 * 3. Si la BD falla, revertir estado local recargando desde la fuente de verdad
 *
 * ## Modo demo / fallback
 * Cuando `organization_id` es un ID demo (`demo-*`) o no válido como UUID, todas las
 * operaciones trabajan con los datos mock definidos en este módulo (`MOCK_CONTROLS`,
 * `MOCK_REFERENTS`, `MOCK_NOTIFICATIONS`). No se realizan consultas a Supabase.
 *
 * ## Sincronización en tiempo real
 * El Provider configura 5 canales de Supabase Realtime que escuchan cambios en:
 * `notifications`, `iso_controls`, `tasks`, `control_assignments`, `evidence`, `audit_trail`.
 * Adicionalmente, hay un polling de respaldo cada 5 minutos para tolerar desconexiones.
 *
 * ## Caché `userToReferentIdCache`
 * Supabase requiere `technical_referents.id` para FKs en tareas y asignaciones, pero la UI
 * trabaja con `users.id`. Esta caché evita una consulta adicional por cada operación que
 * necesite resolver ese mapeo. Se persiste en state para sobrevivir re-renders.
 *
 * ## `controlsRef`
 * Los handlers de Supabase Realtime capturan la función en un closure. Para que puedan
 * acceder al estado más reciente de controles sin necesitar re-suscribirse, se sincroniza
 * el estado en un `useRef` que los closures pueden leer sin capturar un valor stale.
 *
 * @see DataContextType para la API completa expuesta a los consumidores
 * @see useData para el hook de acceso
 * @see useMetrics para métricas calculadas en tiempo real
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase, uploadFile } from '../lib/supabase';
import { logger } from '../utils/logger';
import { isValidUUID } from '../utils/validation';
import {
  createStatusChangeMessage,
  createDueDateChangeMessage,
  createAssignmentMessage,
  createTaskCreatedMessage,
  createTaskCompletedMessage,
  createEvidenceUploadMessage,
  createEvidenceDeleteMessage
} from '../utils/auditMessages';
import { useAuth } from './AuthContext';
import type { 
  ISOControl, 
  Filter, 
  Notification, 
  TechnicalReferent, 
  EmailNotification, 
  Task, 
  Evidence, 
  AuditEntry 
} from '../types/iso';

// =====================================================
// INTERFACES Y TIPOS
// =====================================================

/**
 * Contrato completo del contexto de datos. Define toda la API disponible
 * para los componentes que consumen `useData()`.
 *
 * Las propiedades están agrupadas por responsabilidad para facilitar
 * la navegación y el entendimiento de cada sección.
 */
interface DataContextType {
  // — Estado principal: datos cargados desde Supabase o datos mock en modo demo —
  controls: ISOControl[];
  notifications: Notification[];
  technicalReferents: TechnicalReferent[];
  /** Cola de emails procesados (referencia local; el procesamiento real ocurre en el backend). */
  emailNotifications: EmailNotification[];

  // — Estado de UI —
  /** Criterios de filtrado activos aplicados a `filteredControls`. */
  filter: Filter;
  /** Texto de búsqueda libre aplicado a código, título, descripción, categoría y referentes. */
  searchQuery: string;
  /** `true` mientras se ejecuta la carga inicial o un `refreshData()`. */
  isLoading: boolean;
  /** Último mensaje de error, o `null` si no hay error activo. */
  error: string | null;
  /** `true` cuando la aplicación usa datos mock por falta de conexión o credenciales demo. */
  isDemoMode: boolean;

  // — Funciones de UI —
  setFilter: (filter: Filter) => void;
  setSearchQuery: (query: string) => void;
  /** Limpia el error actual del estado. */
  clearError: () => void;
  /** Recarga controles, referentes y notificaciones en paralelo. */
  refreshData: () => Promise<void>;

  // — Datos derivados (recalculados con useMemo) —
  /** Subconjunto de `controls` que pasa los filtros y la búsqueda activos. */
  filteredControls: ISOControl[];

  // — Controles ISO —
  /** Actualiza campos de un control. Genera audit trail si cambia `status` o `dueDate`. */
  updateControl: (controlId: string, updates: Partial<ISOControl>) => Promise<void>;
  /** Reemplaza las asignaciones de referentes de un control. Crea registros en `technical_referents` para admins si no existen. */
  assignControlToReferent: (controlId: string, referentIds: string[]) => Promise<void>;
  /** Carga un único control con todo su detalle (tareas, evidencias, audit trail). Más eficiente que recargar todos. */
  loadSingleControl: (controlId: string) => Promise<ISOControl | null>;

  // — Referentes técnicos —
  /** Crea un usuario con rol `technical_referent` en `users` y luego su registro en `technical_referents`. */
  addTechnicalReferent: (referent: Omit<TechnicalReferent, 'id' | 'createdAt' | 'assignedControls'>) => Promise<void>;
  /** Actualiza datos del referente en la tabla `users` (el `referentId` es `users.id`). */
  updateTechnicalReferent: (referentId: string, updates: Partial<TechnicalReferent>) => Promise<void>;
  /** Soft-delete: marca `is_active = false` en `users` y `technical_referents`. No elimina el registro. */
  deleteTechnicalReferent: (referentId: string) => Promise<void>;
  /** Crea un registro en `technical_referents` para un admin existente, sin cambiar su rol. */
  enableAdminAsReferent: (userId: string) => Promise<void>;
  /** Elimina el registro en `technical_referents` de un admin. No cambia su rol. */
  disableAdminAsReferent: (userId: string) => Promise<void>;

  // — Tareas —
  addTaskToControl: (controlId: string, task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>, notify?: boolean) => Promise<void>;
  updateTask: (taskId: string, updates: Partial<Task>) => Promise<void>;
  deleteTask: (taskId: string) => Promise<void>;

  // — Evidencias —
  addEvidenceToControl: (
    controlId: string,
    file: File,
    evidenceData: { name: string; type: 'document' | 'screenshot' | 'video' | 'other' }
  ) => Promise<void>;
  /** `notify = true` envía email al uploader cuando la evidencia cambia a `approved` o `rejected`. */
  updateEvidence: (evidenceId: string, updates: Partial<Evidence>, notify?: boolean) => Promise<void>;
  deleteEvidence: (evidenceId: string, controlId: string) => Promise<void>;

  // — Notificaciones in-app —
  createNotification: (data: {
    userId: string;
    title: string;
    message: string;
    type: 'info' | 'warning' | 'error' | 'success';
    actionUrl?: string;
    actionText?: string;
  }) => Promise<void>;
  sendEmailNotification: (notification: Omit<EmailNotification, 'id' | 'sentAt' | 'status' | 'retryCount'>) => Promise<void>;
  markNotificationAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  deleteAllRead: () => Promise<void>;
}

// =====================================================
// CONTEXTO
// =====================================================

/** Contexto interno. Los componentes deben acceder a través de `useData()`, no directamente. */
const DataContext = createContext<DataContextType | undefined>(undefined);

// =====================================================
// DATOS MOCK PARA DESARROLLO
// =====================================================

/**
 * Controles ISO de ejemplo para modo demo y desarrollo offline.
 * Se cargan cuando `organization_id` es un ID demo o no es un UUID válido.
 * Incluyen ejemplos de los tres estados principales (not_started, in_progress, completed)
 * con tareas y audit trail representativos para demostrar todas las funcionalidades de la UI.
 */
const MOCK_CONTROLS: ISOControl[] = [
  {
    id: '1',
    code: 'A.5.1.1',
    title: 'Políticas para la seguridad de la información',
    description: 'Se debe definir un conjunto de políticas para la seguridad de la información, aprobado por la dirección, publicado y comunicado a empleados y partes externas relevantes.',
    category: 'Políticas de Seguridad',
    subcategory: 'Gestión de la seguridad de la información',
    implementation: 'mandatory',
    status: 'in_progress',
    priority: 'high',
    assignedReferents: [],
    dueDate: '2024-12-31',
    evidence: [],
    tasks: [
      {
        id: 't1',
        title: 'Redactar política de seguridad',
        description: 'Crear el documento base de la política de seguridad de la información',
        status: 'in_progress',
        assignee: {
          id: 'r1',
          name: 'Juan Pérez',
          email: 'juan.perez@company.com',
          role: 'technical_referent',
          unit: 'Seguridad TI',
          createdAt: '2024-01-01',
          isActive: true,
          assignedControls: []
        },
        dueDate: '2024-11-30',
        checklist: [
          {
            id: 'c1',
            text: 'Revisar normativas aplicables',
            completed: true,
            required: true
          },
          {
            id: 'c2',
            text: 'Definir alcance de la política',
            completed: false,
            required: true
          }
        ],
        priority: 'high',
        estimatedHours: 40,
        actualHours: 20,
        emailSent: true,
        emailSentAt: '2024-01-15T10:00:00Z',
        createdAt: '2024-01-15T09:00:00Z',
        updatedAt: '2024-01-20T14:30:00Z'
      }
    ],
    auditTrail: [
      {
        id: 'a1',
        action: 'Control creado',
        actionType: 'other' as const,
        user: {
          id: 'u1',
          name: 'Admin Sistema',
          email: 'admin@company.com',
          role: 'admin' as const,
          unit: 'Administración',
          createdAt: '2024-01-01',
          isActive: true
        },
        timestamp: '2024-01-15T08:00:00Z',
        details: {
          tableName: 'iso_controls',
          status: 'not_started',
          priority: 'high'
        }
      },
      {
        id: 'a2',
        action: 'Cambió el estado del control',
        actionType: 'status_change' as const,
        user: {
          id: 'u1',
          name: 'Admin Sistema',
          email: 'admin@company.com',
          role: 'admin' as const,
          unit: 'Administración',
          createdAt: '2024-01-01',
          isActive: true
        },
        timestamp: '2024-01-20T10:30:00Z',
        details: {
          tableName: 'iso_controls',
          changedFields: ['status'],
          oldValues: { status: 'not_started' },
          newValues: { status: 'in_progress' }
        },
        previousValue: 'not_started',
        newValue: 'in_progress',
        impactMetrics: {
          completionChange: 40,
          controlProgress: 40
        }
      },
      {
        id: 'a3',
        action: 'Creó tarea "Definir política de seguridad de la información"',
        actionType: 'task_created' as const,
        user: {
          id: 'u1',
          name: 'Admin Sistema',
          email: 'admin@company.com',
          role: 'admin' as const,
          unit: 'Administración',
          createdAt: '2024-01-01',
          isActive: true
        },
        timestamp: '2024-01-15T09:00:00Z',
        details: {
          tableName: 'tasks',
          task_title: 'Definir política de seguridad de la información',
          priority: 'high',
          assignee_name: 'Juan Pérez',
          due_date: '2024-11-30',
          estimated_hours: 40
        }
      }
    ],
    children: []
  },
  {
    id: '2',
    code: 'A.5.1.2',
    title: 'Revisión de las políticas para la seguridad de la información',
    description: 'Las políticas para la seguridad de la información se deben revisar a intervalos planificados o si ocurren cambios significativos.',
    category: 'Políticas de Seguridad',
    subcategory: 'Gestión de la seguridad de la información',
    implementation: 'mandatory',
    status: 'not_started',
    priority: 'medium',
    assignedReferents: [],
    dueDate: '2024-12-31',
    evidence: [],
    tasks: [],
    auditTrail: [
      {
        id: 'a4',
        action: 'Control creado',
        actionType: 'other' as const,
        user: {
          id: 'u1',
          name: 'Admin Sistema',
          email: 'admin@company.com',
          role: 'admin' as const,
          unit: 'Administración',
          createdAt: '2024-01-01',
          isActive: true
        },
        timestamp: '2024-01-16T09:00:00Z',
        details: {
          tableName: 'iso_controls',
          status: 'not_started',
          priority: 'medium'
        }
      },
      {
        id: 'a5',
        action: 'Asignó el control a 1 referente',
        actionType: 'assignment' as const,
        user: {
          id: 'u1',
          name: 'Admin Sistema',
          email: 'admin@company.com',
          role: 'admin' as const,
          unit: 'Administración',
          createdAt: '2024-01-01',
          isActive: true
        },
        timestamp: '2024-01-17T11:00:00Z',
        details: {
          tableName: 'control_assignments',
          referent_name: 'Equipo SIC',
          referent_email: 'sic@company.com',
          unit: 'Seguridad de la Información',
          assigned_at: '2024-01-17T11:00:00Z'
        },
        impactMetrics: {
          completionChange: 10
        }
      }
    ],
    children: []
  },
  {
    id: '3',
    code: 'A.6.1.1',
    title: 'Roles y responsabilidades para la seguridad de la información',
    description: 'Se deben definir y asignar todas las responsabilidades de la seguridad de la información.',
    category: 'Organización de la Seguridad',
    subcategory: 'Organización interna',
    implementation: 'mandatory',
    status: 'completed',
    priority: 'high',
    assignedReferents: [],
    dueDate: '2024-10-31',
    completionDate: '2024-10-15',
    evidence: [],
    tasks: [],
    auditTrail: [
      {
        id: 'a6',
        action: 'Control creado',
        actionType: 'other' as const,
        user: {
          id: 'u1',
          name: 'Admin Sistema',
          email: 'admin@company.com',
          role: 'admin' as const,
          unit: 'Administración',
          createdAt: '2024-01-01',
          isActive: true
        },
        timestamp: '2024-01-10T10:00:00Z',
        details: {
          tableName: 'iso_controls',
          status: 'not_started',
          priority: 'high'
        }
      },
      {
        id: 'a7',
        action: 'Subió evidencia "Matriz_Roles_Responsabilidades_v2.xlsx"',
        actionType: 'evidence_upload' as const,
        user: {
          id: 'r2',
          name: 'María González',
          email: 'maria.gonzalez@company.com',
          role: 'technical_referent' as const,
          unit: 'Recursos Humanos',
          createdAt: '2024-01-01',
          isActive: true
        },
        timestamp: '2024-10-10T14:30:00Z',
        details: {
          tableName: 'evidence',
          file_name: 'Matriz_Roles_Responsabilidades_v2.xlsx',
          type: 'document',
          size: 1048576,
          uploaded_by: 'María González'
        },
        filesUploaded: [{
          name: 'Matriz_Roles_Responsabilidades_v2.xlsx',
          size: 1048576
        }],
        impactMetrics: {
          completionChange: 5
        }
      },
      {
        id: 'a8',
        action: 'Cambió el estado del control',
        actionType: 'status_change' as const,
        user: {
          id: 'r2',
          name: 'María González',
          email: 'maria.gonzalez@company.com',
          role: 'technical_referent' as const,
          unit: 'Recursos Humanos',
          createdAt: '2024-01-01',
          isActive: true
        },
        timestamp: '2024-10-15T16:00:00Z',
        details: {
          tableName: 'iso_controls',
          changedFields: ['status', 'completionDate'],
          oldValues: { status: 'in_progress', completionDate: null },
          newValues: { status: 'completed', completionDate: '2024-10-15' }
        },
        previousValue: 'in_progress',
        newValue: 'completed',
        impactMetrics: {
          completionChange: 100,
          controlProgress: 100
        }
      }
    ],
    children: []
  }
];

/**
 * Referentes técnicos de ejemplo para modo demo.
 * Incluye un admin y dos referentes técnicos de distintas unidades organizacionales.
 * El admin (`demo-admin-001`) está modelado como un referente asignable para demostrar
 * que los administradores también pueden recibir asignaciones de controles.
 */
const MOCK_REFERENTS: TechnicalReferent[] = [
  {
    id: 'demo-admin-001',
    name: 'Administrador Demo - MINSAL',
    email: 'admin@company.com',
    role: 'admin',
    unit: 'División de Tecnologías de la Información',
    createdAt: '2024-01-01',
    isActive: true,
    assignedControls: []
  },
  {
    id: 'r1',
    name: 'Juan Pérez',
    email: 'juan.perez@company.com',
    role: 'technical_referent',
    unit: 'Seguridad TI',
    createdAt: '2024-01-01',
    isActive: true,
    assignedControls: ['1']
  },
  {
    id: 'r2',
    name: 'María González',
    email: 'maria.gonzalez@company.com',
    role: 'technical_referent',
    unit: 'Recursos Humanos',
    createdAt: '2024-01-01',
    isActive: true,
    assignedControls: []
  }
];

/** Notificaciones de ejemplo para modo demo. Una notificación no leída de tipo info. */
const MOCK_NOTIFICATIONS: Notification[] = [
  {
    id: 'n1',
    title: 'Nueva tarea asignada',
    message: 'Se te ha asignado la tarea "Redactar política de seguridad" para el control A.5.1.1',
    type: 'info',
    timestamp: '2024-01-15T10:00:00Z',
    read: false,
    actionUrl: '/controls/1',
    actionText: 'Ver control'
  }
];

// =====================================================
// PROVIDER
// =====================================================

/**
 * Provider del contexto de datos ISO 27001/27002.
 * Debe envolver toda la aplicación dentro de `AuthProvider` ya que depende
 * de `user` y `dbUserReady` de `AuthContext`.
 *
 * La carga de datos se difiere hasta que `dbUserReady = true`, lo que garantiza
 * que `user.organization_id` es el valor definitivo de PostgreSQL (no solo el JWT).
 */
export function DataProvider({ children }: { children: React.ReactNode }) {
  const { user, dbUserReady } = useAuth();

  // =====================================================
  // ESTADOS PRINCIPALES
  // =====================================================

  const [controls, setControls] = useState<ISOControl[]>([]);

  /**
   * Ref sincronizada con el estado `controls` para uso en closures de Supabase Realtime.
   * Los handlers de eventos se registran una sola vez y capturan el closure inicial;
   * si leyeran directamente el estado, verían siempre el valor en el momento del registro.
   * Al usar la ref, acceden al valor más reciente sin necesidad de re-suscribirse.
   */
  const controlsRef = useRef<ISOControl[]>([]);
  useEffect(() => { controlsRef.current = controls; }, [controls]);

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [technicalReferents, setTechnicalReferents] = useState<TechnicalReferent[]>([]);
  const [emailNotifications, setEmailNotifications] = useState<EmailNotification[]>([]);

  // =====================================================
  // ESTADOS DE UI
  // =====================================================

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** `true` cuando se detecta que Supabase no está disponible y se usaron datos mock. */
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  /** Arrays vacíos = sin filtro activo para esa dimensión. `dateRange` vacío = sin rango. */
  const [filter, setFilter] = useState<Filter>({
    status: [],
    priority: [],
    assignee: [],
    category: [],
    dateRange: { start: '', end: '' }
  });

  // =====================================================
  // CACHÉ DE MAPEO DE IDs (Optimización)
  // =====================================================

  /**
   * Mapeo `users.id → technical_referents.id` para evitar consultas repetidas.
   * La UI trabaja con `users.id` pero las FKs en `tasks` y `control_assignments`
   * referencian `technical_referents.id`. Esta caché resuelve el mapeo en O(1)
   * después del primer acceso. Se almacena en state para persistir entre renders.
   */
  const [userToReferentIdCache, setUserToReferentIdCache] = useState<Map<string, string>>(new Map());

  // =====================================================
  // FUNCIONES DE UTILIDAD
  // =====================================================

  /** Limpia el estado de error actual. Los componentes lo llaman al cerrar el banner de error. */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Centraliza el manejo de errores: loguea con contexto y actualiza el estado de error.
   * No lanza excepción, para que el flujo principal pueda continuar con fallback.
   */
  const handleError = useCallback((error: unknown, context: string) => {
    logger.error(`❌ [DATA] Error en ${context}:`, error);
    const errorMessage = error instanceof Error ? error.message : `Error en ${context}`;
    setError(errorMessage);
  }, []);

  // =====================================================
  // FUNCIONES DE CARGA DE DATOS
  // =====================================================
  
  /**
   * Carga todos los controles ISO desde Supabase con sus relaciones completas.
   *
   * Ejecuta un único SELECT con JOINs a:
   * `iso_categories`, `control_assignments → technical_referents → users`,
   * `tasks → technical_referents → users → task_checklist_items`, `evidence`.
   *
   * El audit trail se carga en una segunda consulta separada (no hay FK directa) y se
   * agrupa por `record_id` usando `reduce()` para asignarlo a cada control.
   *
   * Protecciones:
   * - Timeout de 20 segundos vía `Promise.race()` para no bloquear la UI indefinidamente.
   * - Si falla y ya hay controles en memoria, los mantiene (no sobreescribe con mock).
   * - Si falla y no hay datos previos, activa modo demo (`isDemoMode = true`) con `MOCK_CONTROLS`.
   * - Fallback inmediato a `MOCK_CONTROLS` si `organization_id` es un ID demo o UUID inválido.
   */
  const loadControls = useCallback(async () => {
    if (!user?.organization_id || user.organization_id.startsWith('demo-') || !isValidUUID(user.organization_id)) {
      // Si no hay usuario o el org_id no es un UUID válido, usar datos mock para demo
      setControls(MOCK_CONTROLS);
      return;
    }

    try {
      logger.log('📊 [DATA] Cargando controles ISO desde Supabase...');

      // Crear una promesa con timeout de 20 segundos
      const queryPromise = supabase
        .from('iso_controls')
        .select(`
          *,
          iso_categories (
            code,
            name,
            description
          ),
          control_assignments (
            id,
            technical_referents (
              id,
              users (
                id,
                name,
                email,
                unit,
                avatar_url
              )
            )
          ),
          tasks (
            *,
            technical_referents (
              id,
              users (
                id,
                name,
                email,
                unit,
                avatar_url
              )
            ),
            task_checklist_items (*)
          ),
          evidence (*)
        `)
        .eq('organization_id', user.organization_id)
        .order('code');

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout loading controls')), 20000)
      );

      const { data, error } = await Promise.race([queryPromise, timeoutPromise]) as { data: unknown; error: unknown };

      if (error) {
        logger.error('❌ [DATA] Error cargando controles desde Supabase:', error);

        // ✅ MEJORA: No caer a mock inmediatamente, mostrar error al usuario
        handleError(error, 'cargar controles desde base de datos');

        // ✅ CORRECCIÓN CRÍTICA: Activar modo demo y notificar al usuario
        if (controls.length === 0) {
          logger.warn('⚠️ [DATA] No hay datos previos en memoria, activando MODO DEMO con datos de ejemplo');
          setControls(MOCK_CONTROLS);
          setIsDemoMode(true); // ✅ CRÍTICO: Activar indicador visual de modo demo
        } else {
          logger.log('ℹ️ [DATA] Manteniendo controles existentes en memoria:', controls.length);
        }
        return;
      }

      // Transformar datos de Supabase al formato esperado
      const transformedControls = (data || []).map(control => ({
        id: control.id,
        code: control.code,
        title: control.title,
        description: control.description,
        category: control.iso_categories?.name || 'Sin categoría',
        subcategory: control.subcategory,
        implementation: control.implementation,
        status: control.status,
        priority: control.priority,
        dueDate: control.due_date,
        completionDate: control.completion_date,
        assignedReferents: (control.control_assignments || []).map((assignment: Record<string, unknown>) => ({
          id: assignment.technical_referents?.id,
          name: assignment.technical_referents?.users?.name,
          email: assignment.technical_referents?.users?.email,
          role: 'technical_referent',
          unit: assignment.technical_referents?.users?.unit,
          avatar: assignment.technical_referents?.users?.avatar_url,
          createdAt: assignment.created_at,
          isActive: true,
          assignedControls: []
        })).filter((ref: { id?: string }) => ref.id),
        tasks: (control.tasks || []).map((task: Record<string, unknown>) => ({
          id: task.id,
          title: task.title,
          description: task.description,
          status: task.status,
          priority: task.priority,
          dueDate: task.due_date,
          completedAt: task.completed_at,
          assignee: {
            id: task.technical_referents?.id,
            name: task.technical_referents?.users?.name,
            email: task.technical_referents?.users?.email,
            role: 'technical_referent',
            unit: task.technical_referents?.users?.unit,
            avatar: task.technical_referents?.users?.avatar_url,
            createdAt: task.created_at,
            isActive: true,
            assignedControls: []
          },
          checklist: (task.task_checklist_items || []).map((item: Record<string, unknown>) => ({
            id: item.id,
            text: item.text,
            completed: item.completed,
            required: item.required,
            completedBy: item.completed_by ? {
              id: item.completed_by,
              name: 'Usuario',
              email: '',
              role: 'technical_referent',
              unit: '',
              createdAt: '',
              isActive: true
            } : undefined,
            completedAt: item.completed_at
          })),
          estimatedHours: task.estimated_hours || 0,
          actualHours: task.actual_hours || 0,
          emailSent: task.email_sent || false,
          emailSentAt: task.email_sent_at,
          createdAt: task.created_at,
          updatedAt: task.updated_at
        })),
        evidence: (control.evidence || []).map((evidence: any) => ({
          id: evidence.id,
          name: evidence.name,
          type: evidence.type,
          url: evidence.file_url,
          size: evidence.file_size,
          status: evidence.status,
          uploadedBy: {
            id: evidence.uploaded_by,
            name: 'Usuario',
            email: '',
            role: 'technical_referent',
            unit: '',
            createdAt: '',
            isActive: true
          },
          uploadedAt: evidence.uploaded_at
        })),
        auditTrail: [], // Will be loaded below
        children: []
      }));

      // Cargar audit trail para todos los controles
      logger.log('📋 [DATA] Cargando historial de auditoría...');
      const controlIds = transformedControls.map(c => c.id);

      const { data: auditData, error: auditError } = await supabase
        .from('audit_trail')
        .select('*')
        .eq('table_name', 'iso_controls')
        .in('record_id', controlIds)
        .eq('organization_id', user.organization_id)
        .order('created_at', { ascending: false })
        .limit(500); // Limitar a últimas 500 entradas para performance

      if (!auditError && auditData) {
        // Agrupar audit trail por control
        const auditByControl = auditData.reduce((acc: Record<string, AuditEntry[]>, audit: Record<string, unknown>) => {
          if (!acc[audit.record_id]) {
            acc[audit.record_id] = [];
          }

          // Detectar actionType desde el texto de action
          let actionType: AuditEntry['actionType'] = 'other';
          const actionLower = (audit.action || audit.description || '').toLowerCase();
          const metadata = audit.metadata || {};

          if (actionLower.includes('asign') || actionLower.includes('referente')) {
            actionType = 'assignment';
          } else if (actionLower.includes('estado') || actionLower.includes('completado')) {
            actionType = 'status_change';
          } else if (actionLower.includes('evidencia') || actionLower.includes('cargada')) {
            actionType = 'evidence_upload';
          } else if (actionLower.includes('vencimiento') || actionLower.includes('fecha')) {
            actionType = 'due_date_change';
          } else if (actionLower.includes('tarea') && (actionLower.includes('creada') || actionLower.includes('nueva'))) {
            actionType = 'task_created';
          } else if (actionLower.includes('tarea') && actionLower.includes('completada')) {
            actionType = 'task_completed';
          }

          acc[audit.record_id].push({
            id: audit.id,
            action: audit.action || audit.description || 'Registro actualizado',
            actionType,
            user: {
              id: audit.user_id,
              name: audit.user_name || 'Usuario',
              email: audit.user_email || '',
              role: 'technical_referent' as const,
              unit: '',
              createdAt: '',
              isActive: true
            },
            timestamp: audit.created_at,
            details: metadata.details || metadata || {},
            previousValue: audit.old_values,
            newValue: audit.new_values,
            affectedUsers: metadata.affected_users || metadata.affectedUsers || [],
            filesUploaded: metadata.files_uploaded || metadata.filesUploaded || [],
            impactMetrics: metadata.impact_metrics || metadata.impactMetrics || {}
          });
          return acc;
        }, {});

        // Asignar audit trail a cada control
        transformedControls.forEach(control => {
          control.auditTrail = auditByControl[control.id] || [];
        });

        logger.log(`✅ [DATA] ${auditData.length} entradas de auditoría cargadas`);
      } else if (auditError) {
        logger.warn('⚠️ [DATA] Error cargando audit trail:', auditError);
      }

      setControls(transformedControls);
      logger.log(`✅ [DATA] ${transformedControls.length} controles cargados desde Supabase`);

    } catch (err) {
      logger.warn('⚠️ [DATA] Error cargando controles, usando datos mock:', err);
      handleError(err, 'cargar controles');
      setControls(MOCK_CONTROLS);
    }
  }, [user?.organization_id, handleError]);

  /**
   * Carga un único control ISO desde Supabase con todo su detalle.
   *
   * Más eficiente que `loadControls()` completo cuando solo se modificó un control
   * (ej.: después de crear una tarea, subir evidencia, o actualizar estado).
   * El audit trail se carga en una segunda consulta con JOIN a `users` para obtener
   * el nombre del autor de cada entrada.
   *
   * Manejo de errores:
   * - Status 406: el control no existe o RLS lo bloquea → retorna `null` sin error visible.
   * - Otros errores: actualiza el estado de error y retorna `null`.
   * - Error en audit trail: solo emite advertencia, retorna el control sin historial.
   *
   * @param controlId - UUID del control a cargar
   * @returns El control completo o `null` si no existe o hay error
   */
  const loadSingleControl = useCallback(async (controlId: string): Promise<ISOControl | null> => {
    if (!user?.organization_id || user.organization_id.startsWith('demo-')) {
      // En modo demo, buscar en MOCK_CONTROLS
      return MOCK_CONTROLS.find(c => c.id === controlId) || null;
    }

    try {
      logger.log(`📊 [DATA] Cargando control individual ${controlId} desde Supabase...`);

      const { data, error, status } = await supabase
        .from('iso_controls')
        .select(`
          *,
          iso_categories (
            code,
            name,
            description
          ),
          control_assignments (
            id,
            technical_referents (
              id,
              users (
                id,
                name,
                email,
                unit,
                avatar_url
              )
            )
          ),
          tasks (
            *,
            technical_referents (
              id,
              users (
                id,
                name,
                email,
                unit,
                avatar_url
              )
            ),
            task_checklist_items (*)
          ),
          evidence (
            *,
            uploaded_by_user:users!uploaded_by (
              id,
              name,
              email
            )
          )
        `)
        .eq('id', controlId)
        .eq('organization_id', user.organization_id)
        .single();

      if (error) {
        if (status === 406) {
          logger.error('❌ [DATA] Control no encontrado o sin permisos');
          return null;
        }
        throw error;
      }

      if (!data) {
        logger.warn('⚠️ [DATA] Control no encontrado');
        return null;
      }

      // Cargar audit trail por separado (no se puede hacer JOIN porque no hay FK directa)
      logger.log(`📋 [DATA] Cargando audit trail para control ${controlId}...`);
      const { data: auditData, error: auditError } = await supabase
        .from('audit_trail')
        .select(`
          *,
          users (
            id,
            name,
            email,
            unit
          )
        `)
        .eq('table_name', 'iso_controls')
        .eq('record_id', controlId)
        .eq('organization_id', user.organization_id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (auditError) {
        logger.warn('⚠️ [DATA] Error cargando audit trail, continuando sin él:', auditError);
      }

      // Transformar datos (usar misma lógica que loadControls)
      const transformedControl: ISOControl = {
        id: data.id,
        code: data.code,
        title: data.title,
        description: data.description || '',
        category: data.category,
        subcategory: data.subcategory || '',
        implementation: data.implementation as 'mandatory' | 'recommended' | 'optional',
        status: data.status as 'not_started' | 'in_progress' | 'review' | 'completed' | 'non_compliant',
        priority: data.priority as 'low' | 'medium' | 'high' | 'critical',
        dueDate: data.due_date,
        completionDate: data.completion_date,
        parentId: data.parent_id,
        children: [],

        // Transformar referentes asignados
        assignedReferents: (data.control_assignments || [])
          .filter((ca: Record<string, unknown>) => ca.technical_referents?.users)
          .map((ca: Record<string, unknown>) => ({
            id: ca.technical_referents.users.id,
            name: ca.technical_referents.users.name,
            email: ca.technical_referents.users.email,
            role: 'technical_referent' as const,
            unit: ca.technical_referents.users.unit || '',
            createdAt: new Date().toISOString(),
            isActive: true,
            assignedControls: []
          })),

        // Transformar tareas
        tasks: (data.tasks || []).map((task: Record<string, unknown>) => ({
          id: task.id,
          title: task.title,
          description: task.description || '',
          status: task.status as 'pending' | 'in_progress' | 'completed',
          priority: task.priority as 'low' | 'medium' | 'high' | 'critical',
          dueDate: task.due_date,
          completedAt: task.completed_at,
          assignee: task.technical_referents?.users ? {
            id: task.technical_referents.users.id,
            name: task.technical_referents.users.name,
            email: task.technical_referents.users.email,
            role: 'technical_referent' as const,
            unit: task.technical_referents.users.unit || '',
            createdAt: new Date().toISOString(),
            isActive: true,
            assignedControls: []
          } : undefined as unknown as TechnicalReferent,
          checklist: (task.task_checklist_items || []).map((item: Record<string, unknown>) => ({
            id: item.id,
            text: item.text,
            completed: item.completed,
            required: item.required,
            completedAt: item.completed_at,
            completedBy: item.completed_by ? {
              id: item.completed_by,
              name: 'Usuario',
              email: '',
              role: 'technical_referent' as const,
              unit: '',
              createdAt: new Date().toISOString(),
              isActive: true
            } : undefined
          })),
          estimatedHours: task.estimated_hours || 0,
          actualHours: task.actual_hours || 0,
          emailSent: task.email_sent || false,
          emailSentAt: task.email_sent_at,
          createdAt: task.created_at,
          updatedAt: task.updated_at
        })),

        // Transformar evidencias
        evidence: (data.evidence || []).map((ev: Record<string, unknown>) => ({
          id: ev.id,
          name: ev.name,
          type: ev.type as 'document' | 'screenshot' | 'video' | 'other',
          url: ev.file_url,
          size: ev.file_size || 0,
          status: ev.status as 'pending' | 'approved' | 'rejected',
          uploadedBy: ev.uploaded_by_user ? {
            id: ev.uploaded_by_user.id,
            name: ev.uploaded_by_user.name,
            email: ev.uploaded_by_user.email
          } : { id: '', name: '', email: '' },
          uploadedAt: ev.uploaded_at
        })),

        // Transformar audit trail
        // El esquema real usa: table_name, record_id, action, old_values, new_values, metadata
        auditTrail: (auditData || []).map((audit: Record<string, unknown>) => {
          // Detectar actionType desde el texto de action y metadata
          let actionType: AuditEntry['actionType'] = 'other';
          const actionLower = (audit.action || audit.description || '').toLowerCase();
          const metadata = audit.metadata || {};

          if (actionLower.includes('asign') || actionLower.includes('referente')) {
            actionType = 'assignment';
          } else if (actionLower.includes('estado') || actionLower.includes('completado')) {
            actionType = 'status_change';
          } else if (actionLower.includes('evidencia') || actionLower.includes('cargada')) {
            actionType = 'evidence_upload';
          } else if (actionLower.includes('vencimiento') || actionLower.includes('fecha')) {
            actionType = 'due_date_change';
          } else if (actionLower.includes('tarea') && actionLower.includes('creada')) {
            actionType = 'task_created';
          } else if (actionLower.includes('tarea') && actionLower.includes('completada')) {
            actionType = 'task_completed';
          }

          return {
            id: audit.id,
            action: audit.action || audit.description || 'Registro actualizado',
            actionType,
            user: audit.users ? {
              id: audit.users.id,
              name: audit.users.name || audit.user_name || 'Usuario',
              email: audit.users.email || audit.user_email || '',
              role: audit.users.role || 'technical_referent' as const,
              unit: audit.users.unit || '',
              createdAt: new Date().toISOString(),
              isActive: true
            } : {
              id: audit.user_id || '',
              name: audit.user_name || 'Usuario',
              email: audit.user_email || '',
              role: 'technical_referent' as const,
              unit: '',
              createdAt: new Date().toISOString(),
              isActive: true
            },
            timestamp: audit.created_at || audit.timestamp || new Date().toISOString(),
            details: metadata.details || metadata || {},
            previousValue: audit.old_values,
            newValue: audit.new_values,
            affectedUsers: metadata.affected_users || metadata.affectedUsers || [],
            filesUploaded: metadata.files_uploaded || metadata.filesUploaded || [],
            impactMetrics: metadata.impact_metrics || metadata.impactMetrics || {}
          };
        })
      };

      logger.log(`✅ [DATA] Control ${controlId} cargado exitosamente`);
      return transformedControl;

    } catch (err) {
      logger.error(`❌ [DATA] Error cargando control ${controlId}:`, err);
      handleError(err, `cargar control ${controlId}`);
      return null;
    }
  }, [user?.organization_id, handleError]);

  /**
   * Carga todos los referentes técnicos disponibles para asignación.
   *
   * La consulta principal trae registros de `technical_referents` con JOIN a `users`
   * filtrados por organización. Luego se hace una segunda consulta para incluir los
   * usuarios con rol `admin` que no tienen registro en `technical_referents` (son
   * asignables pero no aparecen en la tabla de referentes).
   *
   * Los admin se incluyen por defecto porque en organizaciones pequeñas el administrador
   * del sistema también puede ser asignado como responsable técnico de controles.
   *
   * Nota: el campo `id` del resultado es `users.id`, no `technical_referents.id`.
   * Esta uniformidad permite que la UI use el mismo ID para operaciones de usuario y de referente.
   */
  const loadTechnicalReferents = useCallback(async () => {
    if (!user?.organization_id || user.organization_id.startsWith('demo-')) {
      setTechnicalReferents(MOCK_REFERENTS);
      return;
    }

    try {
      logger.log('👥 [DATA] Cargando referentes técnicos desde Supabase...');

      const queryPromise = supabase
        .from('technical_referents')
        .select(`
          *,
          users (
            id,
            name,
            email,
            role,
            unit,
            avatar_url,
            is_active
          ),
          control_assignments (
            control_id
          )
        `)
        .eq('users.organization_id', user.organization_id);

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout loading referents')), 20000)
      );

      const { data, error } = await Promise.race([queryPromise, timeoutPromise]) as { data: unknown; error: unknown };

      if (error) {
        logger.error('❌ [DATA] Error cargando referentes desde Supabase:', error);
        handleError(error, 'cargar referentes técnicos');

        // ✅ MEJORA: Mantener referentes existentes si los hay
        if (technicalReferents.length === 0) {
          logger.warn('⚠️ [DATA] No hay referentes previos, usando datos mock como fallback');
          setTechnicalReferents(MOCK_REFERENTS);
        } else {
          logger.log('ℹ️ [DATA] Manteniendo referentes existentes en memoria:', technicalReferents.length);
        }
        return;
      }

      const transformedReferents = (data || []).map(referent => ({
        id: referent.users?.id || referent.id, // Usar users.id como identificador principal
        name: referent.users?.name || '',
        email: referent.users?.email || '',
        role: ((referent.users?.role as string) === 'admin' ? 'admin' : 'technical_referent') as 'admin' | 'technical_referent',
        unit: referent.users?.unit || '',
        avatar: referent.users?.avatar_url,
        createdAt: referent.created_at,
        isActive: referent.users?.is_active || false,
        assignedControls: (referent.control_assignments || []).map((assignment: Record<string, unknown>) => assignment.control_id as string)
      }));

      // También incluir admins como referentes asignables
      const existingIds = new Set(transformedReferents.map(r => r.id));
      const { data: adminUsers, error: adminError } = await supabase
        .from('users')
        .select('id, name, email, role, unit, avatar_url, is_active, created_at')
        .eq('organization_id', user.organization_id)
        .eq('role', 'admin')
        .neq('is_active', false); // NULL se trata como activo; solo excluir is_active = false explícito

      if (adminError) {
        logger.warn('⚠️ [DATA] Error cargando admins como referentes:', adminError);
      }

      const adminReferents = (adminUsers || [])
        .filter(u => !existingIds.has(u.id))
        .map(u => ({
          id: u.id,
          name: u.name || '',
          email: u.email || '',
          role: 'admin' as const,
          unit: u.unit || '',
          avatar: u.avatar_url,
          createdAt: u.created_at,
          isActive: u.is_active !== false, // null o true → activo
          assignedControls: [],
        }));

      const allReferents = [...transformedReferents, ...adminReferents];
      setTechnicalReferents(allReferents);
      logger.log(`✅ [DATA] ${allReferents.length} referentes cargados desde Supabase (${adminReferents.length} admins incluidos)`);

    } catch (err) {
      logger.warn('⚠️ [DATA] Error cargando referentes, usando datos mock:', err);
      handleError(err, 'cargar referentes');
      setTechnicalReferents(MOCK_REFERENTS);
    }
  }, [user?.organization_id, handleError]);

  /**
   * Carga las 50 notificaciones más recientes del usuario autenticado.
   * Limitado a 50 entradas para mantener el payload de red razonable.
   * Las notificaciones más antiguas se descartan (el usuario ve las más relevantes).
   * La suscripción Realtime agrega nuevas notificaciones en tiempo real sin recargar.
   */
  const loadNotifications = useCallback(async () => {
    if (!user?.id || user.id.startsWith('demo-')) {
      setNotifications(MOCK_NOTIFICATIONS);
      return;
    }

    try {
      logger.log('🔔 [DATA] Cargando notificaciones desde Supabase...');

      const queryPromise = supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout loading notifications')), 20000)
      );

      const { data, error } = await Promise.race([queryPromise, timeoutPromise]) as { data: unknown; error: unknown };

      if (error) {
        logger.error('❌ [DATA] Error cargando notificaciones desde Supabase:', error);
        handleError(error, 'cargar notificaciones');

        // ✅ MEJORA: Mantener notificaciones existentes si las hay
        if (notifications.length === 0) {
          logger.warn('⚠️ [DATA] No hay notificaciones previas, usando datos mock como fallback');
          setNotifications(MOCK_NOTIFICATIONS);
        } else {
          logger.log('ℹ️ [DATA] Manteniendo notificaciones existentes en memoria:', notifications.length);
        }
        return;
      }

      const transformedNotifications = (data || []).map(notification => ({
        id: notification.id,
        title: notification.title,
        message: notification.message,
        type: notification.type,
        timestamp: notification.created_at,
        read: notification.read,
        actionUrl: notification.action_url,
        actionText: notification.action_text
      }));

      setNotifications(transformedNotifications);
      logger.log(`✅ [DATA] ${transformedNotifications.length} notificaciones cargadas desde Supabase`);

    } catch (err) {
      logger.warn('⚠️ [DATA] Error cargando notificaciones, usando datos mock:', err);
      handleError(err, 'cargar notificaciones');
      setNotifications(MOCK_NOTIFICATIONS);
    }
  }, [user?.id, handleError]);

  /**
   * Recarga todos los datos en paralelo (controles, referentes, notificaciones).
   * Usa `Promise.all()` para minimizar el tiempo total de carga.
   * Activa `isLoading` durante el proceso para mostrar estado de carga en la UI.
   * Llamado por el polling de respaldo cada 5 minutos y por botones manuales de refresco.
   */
  const refreshData = useCallback(async () => {
    try {
      setIsLoading(true);
      logger.log('🔄 [DATA] Refrescando todos los datos...');

      await Promise.all([
        loadControls(),
        loadTechnicalReferents(),
        loadNotifications()
      ]);

      logger.log('✅ [DATA] Todos los datos refrescados');

    } catch (err) {
      handleError(err, 'refrescar datos');
    } finally {
      setIsLoading(false);
    }
  }, [loadControls, loadTechnicalReferents, loadNotifications, handleError]);

  // =====================================================
  // FUNCIONES DE AUDIT TRAIL
  // =====================================================

  /**
   * Construye una entrada de audit trail en memoria con datos del usuario actual.
   * No persiste en DB; para eso usar `saveAuditEntryToDB()`.
   * El `id` se genera localmente con timestamp + random para ser único incluso en modo demo.
   * Lanza error si no hay usuario autenticado (no debería ocurrir en flujos normales).
   *
   * @param action - Texto legible del cambio (ej.: "Cambió el estado a Completado")
   * @param actionType - Categoría del cambio para filtros y visualización
   * @param options - Metadatos opcionales: usuarios afectados, archivos, métricas de impacto
   * @returns AuditEntry lista para agregar al estado local o persistir en DB
   */
  const createAuditEntry = useCallback((
    action: string,
    actionType: AuditEntry['actionType'],
    options: {
      previousValue?: unknown;
      newValue?: unknown;
      affectedUsers?: TechnicalReferent[];
      filesUploaded?: { name: string; size: number }[];
      impactMetrics?: {
        completionChange?: number;
        categoryProgress?: number;
        controlProgress?: number;
      };
    } = {}
  ): AuditEntry => {
    if (!user) {
      throw new Error('Usuario no autenticado');
    }

    return {
      id: `audit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      action,
      actionType,
      user: {
        id: user.id,
        name: user.name,
        email: user.email,
        role: user.role,
        unit: user.unit || '',
        createdAt: user.createdAt || new Date().toISOString(),
        isActive: user.isActive !== undefined ? user.isActive : true
      },
      timestamp: new Date().toISOString(),
      details: {},
      ...options
    };
  }, [user]);

  /**
   * Persiste una entrada de audit trail en la tabla `audit_trail` de PostgreSQL.
   * Es una operación secundaria (no bloquea el flujo principal): si falla, solo loguea el error.
   * Los metadatos estructurados se guardan en la columna JSONB `metadata`.
   *
   * @param controlId - UUID del control al que pertenece la entrada
   * @param auditEntry - Entrada construida previamente con `createAuditEntry()`
   */
  const saveAuditEntryToDB = useCallback(async (
    controlId: string,
    auditEntry: AuditEntry
  ) => {
    if (!user?.organization_id) return;

    try {
      const { error } = await supabase.from('audit_trail').insert([{
        organization_id: user.organization_id,
        table_name: 'iso_controls',
        record_id: controlId,
        action: auditEntry.action,
        user_id: user.id,
        user_name: user.name,
        user_email: user.email,
        old_values: auditEntry.previousValue ? { value: auditEntry.previousValue } : null,
        new_values: auditEntry.newValue ? { value: auditEntry.newValue } : null,
        metadata: {
          actionType: auditEntry.actionType,
          affectedUsers: auditEntry.affectedUsers?.map(u => ({ id: u.id, name: u.name, email: u.email })),
          filesUploaded: auditEntry.filesUploaded,
          impactMetrics: auditEntry.impactMetrics,
          details: auditEntry.details
        }
      }]);

      if (error) {
        logger.error('❌ [AUDIT] Error guardando en PostgreSQL:', error);
      } else {
        logger.log(`✅ [AUDIT] Entrada guardada en PostgreSQL: ${auditEntry.id}`);
      }
    } catch (err) {
      logger.error('❌ [AUDIT] Excepción guardando audit trail:', err);
    }
  }, [user]);

  // =====================================================
  // FUNCIONES DE CONTROLES
  // =====================================================

  /**
   * Actualiza campos de un control ISO con patrón optimista.
   *
   * Genera automáticamente una entrada de audit trail si cambia `status` o `dueDate`,
   * incluyendo métricas de impacto (completion change según el nuevo estado).
   * Las actualizaciones se aplican localmente antes de confirmar en PostgreSQL.
   * Si la BD falla, recarga el estado desde la fuente de verdad.
   *
   * `controlsRef` se usa para leer el estado actual sin capturar un closure stale,
   * permitiendo obtener el valor `currentControl.status` incluso si el estado
   * cambió varias veces desde que se creó el callback.
   *
   * @param controlId - UUID del control a actualizar
   * @param updates - Campos a modificar (solo se envían a PostgreSQL los campos reconocidos)
   */
  const updateControl = useCallback(async (controlId: string, updates: Partial<ISOControl>) => {
    try {
      logger.log(`📝 [DATA] Actualizando control ${controlId}:`, updates);

      // Obtener el control actual para comparar cambios
      let currentControl = controlsRef.current.find(c => c.id === controlId);
      if (!currentControl) {
        // Ref puede estar stale — recargar controles y reintentar
        await loadControls();
        currentControl = controlsRef.current.find(c => c.id === controlId);
        if (!currentControl) {
          throw new Error('Control no encontrado');
        }
      }

      // Crear audit trail enriquecido si cambió el estado
      let newAuditEntry: AuditEntry | null = null;
      if (updates.status && updates.status !== currentControl.status) {
        newAuditEntry = createAuditEntry(
          createStatusChangeMessage(currentControl.status, updates.status),
          'status_change',
          {
            previousValue: currentControl.status,
            newValue: updates.status,
            impactMetrics: {
              completionChange: updates.status === 'completed' ? 100 :
                               updates.status === 'review' ? 75 :
                               updates.status === 'in_progress' ? 40 : 0
            }
          }
        );
        // 🆕 FASE 1: Guardar en PostgreSQL
        await saveAuditEntryToDB(controlId, newAuditEntry);
      } else if (updates.dueDate && updates.dueDate !== currentControl.dueDate) {
        newAuditEntry = createAuditEntry(
          createDueDateChangeMessage(currentControl.dueDate, updates.dueDate),
          'due_date_change',
          {
            previousValue: currentControl.dueDate,
            newValue: updates.dueDate
          }
        );
        // 🆕 FASE 1: Guardar en PostgreSQL
        await saveAuditEntryToDB(controlId, newAuditEntry);
      }

      // Actualizar localmente primero para mejor UX
      setControls(prev => prev.map(c =>
        c.id === controlId
          ? {
              ...c,
              ...updates,
              auditTrail: newAuditEntry ? [...c.auditTrail, newAuditEntry] : c.auditTrail
            }
          : c
      ));

      if (user?.organization_id) {
        const { error } = await supabase
          .from('iso_controls')
          .update({
            status: updates.status,
            priority: updates.priority,
            due_date: updates.dueDate,
            completion_date: updates.completionDate,
            updated_at: new Date().toISOString()
          })
          .eq('id', controlId);

        if (error) {
          throw error;
        }
      }

      logger.log(`✅ [DATA] Control ${controlId} actualizado`);

    } catch (err) {
      handleError(err, `actualizar control ${controlId}`);
      // Revertir cambios en caso de error
      await loadControls();
    }
  }, [handleError, loadControls, user?.organization_id, createAuditEntry, saveAuditEntryToDB]);

  /**
   * Asigna (o reasigna) referentes técnicos a un control ISO.
   *
   * El flujo en PostgreSQL es:
   * 1. Eliminar todas las asignaciones existentes del control en `control_assignments`.
   * 2. Buscar los `technical_referents.id` correspondientes a los `users.id` recibidos.
   * 3. Si algún admin no tiene registro en `technical_referents`, crearlo automáticamente.
   * 4. Insertar las nuevas asignaciones con los `technical_referents.id` correctos.
   * 5. Actualizar el estado del control a `in_progress` si era `not_started`.
   *
   * Pasos 2 y 3 se hacen en paralelo con Promise.all para minimizar latencia.
   * Los `referentIds` recibidos son `users.id`, pero PostgreSQL requiere `technical_referents.id`;
   * esta función maneja la traducción internamente.
   *
   * @param controlId - UUID del control a asignar
   * @param referentIds - Lista de `users.id` de los referentes a asignar (puede ser vacía para desasignar)
   */
  const assignControlToReferent = useCallback(async (controlId: string, referentIds: string[]) => {
    try {
      logger.log(`👥 [DATA] Asignando control ${controlId} a referentes:`, referentIds);

      const assignedReferents = technicalReferents.filter(r => referentIds.includes(r.id));

      // Crear audit trail enriquecido para asignación
      const auditEntry = createAuditEntry(
        createAssignmentMessage(assignedReferents),
        'assignment',
        {
          affectedUsers: assignedReferents,
          impactMetrics: {
            completionChange: 10 // Asignar un control suma un pequeño progreso
          }
        }
      );

      // 🆕 FASE 1: Guardar en PostgreSQL
      await saveAuditEntryToDB(controlId, auditEntry);

      // Actualizar localmente
      setControls(prev => prev.map(c =>
        c.id === controlId
          ? {
              ...c,
              assignedReferents,
              status: c.status === 'not_started' ? 'in_progress' : c.status,
              assignedAt: new Date().toISOString(),
              auditTrail: [...c.auditTrail, auditEntry]
            }
          : c
      ));

      if (user?.organization_id) {
        // Eliminar asignaciones existentes y (si hay referentes) buscar sus IDs en paralelo
        const [, fetchResult] = await Promise.all([
          supabase.from('control_assignments').delete().eq('control_id', controlId),
          referentIds.length > 0
            ? supabase.from('technical_referents').select('id, user_id').in('user_id', referentIds)
            : Promise.resolve({ data: null, error: null }),
        ]);

        // Crear nuevas asignaciones
        if (referentIds.length > 0) {
          const { data: technicalReferentsData, error: fetchError } = fetchResult;

          if (fetchError) {
            throw fetchError;
          }

          // Detectar admins sin registro en technical_referents y crearlo automáticamente
          const foundUserIds = new Set((technicalReferentsData || []).map((r: Record<string, unknown>) => r.user_id as string));
          const missingUserIds = referentIds.filter(id => !foundUserIds.has(id));

          if (missingUserIds.length > 0) {
            const { data: adminCheck } = await supabase
              .from('users')
              .select('id')
              .in('id', missingUserIds)
              .eq('role', 'admin');

            const confirmedAdminIds = (adminCheck || []).map((u: Record<string, unknown>) => u.id as string);
            if (confirmedAdminIds.length > 0) {
              logger.log(`🔧 [DATA] Creando registros technical_referents para admins:`, confirmedAdminIds);
              await supabase.from('technical_referents').insert(
                confirmedAdminIds.map(uid => ({
                  user_id: uid,
                  specializations: [],
                  max_assigned_controls: 10
                }))
              );
            }
          }

          // Re-fetch para incluir los registros recién creados
          const { data: allReferentsData, error: refetchError } = await supabase
            .from('technical_referents')
            .select('id, user_id')
            .in('user_id', referentIds);

          if (refetchError) {
            throw refetchError;
          }

          if (!allReferentsData || allReferentsData.length === 0) {
            throw new Error('No se encontraron referentes técnicos para los IDs especificados');
          }

          // Crear las asignaciones usando technical_referents.id
          const assignments = allReferentsData.map((ref: Record<string, unknown>) => ({
            control_id: controlId,
            referent_id: ref.id, // Usar technical_referents.id, no users.id
            assigned_by: user.id,
            assigned_at: new Date().toISOString()
          }));

          const { error } = await supabase
            .from('control_assignments')
            .insert(assignments);

          if (error) {
            throw error;
          }
        }

        // Actualizar estado del control
        await supabase
          .from('iso_controls')
          .update({
            status: 'in_progress',
            assigned_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', controlId);
      }

      logger.log(`✅ [DATA] Control ${controlId} asignado exitosamente`);

    } catch (err) {
      handleError(err, `asignar control ${controlId}`);
      await loadControls();
    }
  }, [technicalReferents, handleError, user, loadControls, createAuditEntry, saveAuditEntryToDB]);

  // =====================================================
  // FUNCIONES DE REFERENTES
  // =====================================================
  
  /**
   * Crea un nuevo referente técnico en dos pasos atómicos:
   * 1. Inserta el usuario en la tabla `users` (con rol `technical_referent`).
   * 2. Inserta su registro en `technical_referents` con el `user_id` recién creado.
   *
   * Si el paso 2 falla, se revierte el paso 1 eliminando el usuario (rollback manual,
   * ya que Supabase client-side no soporta transacciones).
   * El `id` del referente resultante es `users.id` (no `technical_referents.id`).
   *
   * En modo demo, crea el referente solo en memoria local con un ID temporal.
   */
  const addTechnicalReferent = useCallback(async (
    referentData: Omit<TechnicalReferent, 'id' | 'createdAt' | 'assignedControls'>
  ) => {
    try {
      logger.log('👤 [DATA] Agregando referente técnico:', referentData);

      if (user?.organization_id) {
        // Crear registro en tabla users (el ID se genera automáticamente con gen_random_uuid())
        const { data: userData, error: userError } = await supabase
          .from('users')
          .insert([{
            organization_id: user.organization_id,
            name: referentData.name,
            email: referentData.email,
            role: 'technical_referent',
            unit: referentData.unit,
            is_active: referentData.isActive
          }])
          .select()
          .single();

        if (userError) {
          throw userError;
        }

        logger.log('✅ [DATA] Usuario creado con ID:', userData.id);

        // Crear referente técnico
        const { data: referentDbData, error: referentError } = await supabase
          .from('technical_referents')
          .insert([{
            user_id: userData.id,
            specializations: [],
            max_assigned_controls: 10
          }])
          .select()
          .single();

        if (referentError) {
          // Si falla, eliminar el usuario creado
          await supabase.from('users').delete().eq('id', userData.id);
          throw referentError;
        }

        // Actualizar estado local
        const newReferent: TechnicalReferent = {
          id: userData.id,
          name: referentData.name,
          email: referentData.email,
          role: 'technical_referent',
          unit: referentData.unit,
          isActive: referentData.isActive,
          createdAt: referentDbData.created_at,
          assignedControls: []
        };

        setTechnicalReferents(prev => [...prev, newReferent]);

        logger.log(`✅ [DATA] Referente ${referentData.name} creado completamente`);
      } else {
        // Modo demo
        const newReferent: TechnicalReferent = {
          ...referentData,
          id: `r${Date.now()}`,
          createdAt: new Date().toISOString(),
          assignedControls: []
        };

        setTechnicalReferents(prev => [...prev, newReferent]);
      }

      logger.log(`✅ [DATA] Referente ${referentData.name} agregado`);

    } catch (err) {
      handleError(err, 'agregar referente técnico');
    }
  }, [handleError, user?.organization_id]);

  /**
   * Actualiza los datos de un referente técnico.
   * El parámetro `referentId` es `users.id` (no `technical_referents.id`),
   * por lo que la actualización se hace en la tabla `users`.
   * La cláusula `.eq('organization_id', ...)` es una salvaguarda adicional de seguridad
   * para prevenir actualizar usuarios de otra organización si se filtra incorrectamente.
   */
  const updateTechnicalReferent = useCallback(async (
    referentId: string,
    updates: Partial<TechnicalReferent>
  ) => {
    try {
      logger.log(`📝 [DATA] Actualizando referente ${referentId}:`, updates);

      // Actualizar localmente primero
      setTechnicalReferents(prev => prev.map(r =>
        r.id === referentId ? { ...r, ...updates } : r
      ));

      if (user?.organization_id) {
        // Actualizar en la tabla users - referentId ya es users.id
        const { error } = await supabase
          .from('users')
          .update({
            name: updates.name,
            email: updates.email,
            unit: updates.unit,
            is_active: updates.isActive,
            updated_at: new Date().toISOString()
          })
          .eq('id', referentId) // referentId es users.id
          .eq('organization_id', user.organization_id); // Seguridad adicional

        if (error) {
          throw error;
        }
      }

      logger.log(`✅ [DATA] Referente ${referentId} actualizado`);

    } catch (err) {
      handleError(err, `actualizar referente ${referentId}`);
      await loadTechnicalReferents();
    }
  }, [handleError, user?.organization_id, loadTechnicalReferents]);

  /**
   * Desactiva un referente técnico usando soft-delete (no elimina el registro).
   * Marca `is_active = false` en `users` y en `technical_referents`.
   * El referente deja de aparecer en las listas de asignación pero sus datos históricos
   * (asignaciones pasadas, tareas completadas) se conservan para auditoría.
   * El error en `technical_referents` no es crítico (el registro es opcional para admins).
   */
  const deleteTechnicalReferent = useCallback(async (referentId: string) => {
    try {
      logger.log(`🗑️ [DATA] Desactivando referente ${referentId} (soft delete)`);

      // Actualizar localmente primero - marcar como inactivo
      setTechnicalReferents(prev =>
        prev.map(r => r.id === referentId ? { ...r, isActive: false } : r)
      );

      if (user?.organization_id) {
        // Marcar usuario como inactivo en lugar de eliminarlo
        const { error: userError } = await supabase
          .from('users')
          .update({ is_active: false })
          .eq('id', referentId)
          .eq('organization_id', user.organization_id); // Seguridad adicional

        if (userError) {
          throw userError;
        }

        // Actualizar también el registro en technical_referents si existe
        const { error: refError } = await supabase
          .from('technical_referents')
          .update({ is_active: false })
          .eq('user_id', referentId);

        // No lanzar error si technical_referents no existe, es opcional
        if (refError) {
          logger.warn('[DATA] Error actualizando technical_referents (no crítico):', refError);
        }
      }

      logger.log(`✅ [DATA] Referente ${referentId} desactivado (puede reactivarse)`);

    } catch (err) {
      handleError(err, `desactivar referente ${referentId}`);
      await loadTechnicalReferents();
    }
  }, [handleError, user?.organization_id, loadTechnicalReferents]);

  /**
   * Habilita a un admin existente como referente técnico asignable.
   * Solo crea un registro en technical_referents; NO cambia el rol del usuario.
   */
  const enableAdminAsReferent = useCallback(async (userId: string) => {
    try {
      logger.log(`🔧 [DATA] Habilitando admin ${userId} como referente técnico`);

      if (user?.organization_id && !user.organization_id.startsWith('demo-')) {
        const { error } = await supabase
          .from('technical_referents')
          .insert([{
            user_id: userId,
            specializations: [],
            max_assigned_controls: 10
          }]);

        if (error) throw error;
        await loadTechnicalReferents();
      } else {
        // Modo demo: marcar al referente mock existente o agregar si no existe
        setTechnicalReferents(prev => {
          if (prev.find(r => r.id === userId)) return prev;
          return prev; // en demo no hay admins mock por defecto, se ignora
        });
      }

      logger.log(`✅ [DATA] Admin ${userId} habilitado como referente técnico`);
    } catch (err) {
      handleError(err, 'habilitar admin como referente');
    }
  }, [user?.organization_id, loadTechnicalReferents, handleError]);

  /**
   * Deshabilita a un admin como referente técnico.
   * Elimina su registro en technical_referents; NO cambia el rol del usuario.
   */
  const disableAdminAsReferent = useCallback(async (userId: string) => {
    try {
      logger.log(`🔧 [DATA] Deshabilitando admin ${userId} como referente técnico`);

      if (user?.organization_id && !user.organization_id.startsWith('demo-')) {
        const { error } = await supabase
          .from('technical_referents')
          .delete()
          .eq('user_id', userId);

        if (error) throw error;
        setTechnicalReferents(prev => prev.filter(r => r.id !== userId));
      } else {
        setTechnicalReferents(prev => prev.filter(r => r.id !== userId));
      }

      logger.log(`✅ [DATA] Admin ${userId} deshabilitado como referente técnico`);
    } catch (err) {
      handleError(err, 'deshabilitar admin como referente');
    }
  }, [user?.organization_id, handleError]);

  // =====================================================
  // FUNCIONES DE NOTIFICACIONES POR EMAIL
  // =====================================================

  /**
   * Consulta las preferencias de notificación por email del usuario antes de enviar.
   * Si no hay preferencias configuradas, asume "enviar todo inmediatamente" como valor por defecto.
   *
   * Retorna `shouldSend = false` si el usuario deshabilitó todos los emails o el tipo específico.
   * Retorna `queueType` para indicar si debe ir al canal inmediato, digest diario o semanal.
   * Los errores de consulta se silencian y se permite el envío por defecto para no bloquear.
   *
   * @param userId - UUID del usuario destinatario
   * @param notificationType - Tipo de notificación (ej.: 'control_assignment', 'task_assigned')
   */
  const checkEmailPreferences = useCallback(async (
    userId: string,
    notificationType: string
  ): Promise<{ shouldSend: boolean; queueType?: 'immediate' | 'daily' | 'weekly' }> => {
    try {
      // Obtener preferencias del usuario
      const { data: prefs, error } = await supabase
        .from('email_preferences')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error || !prefs) {
        // Si no hay preferencias, asumir valores por defecto (enviar todo)
        return { shouldSend: true, queueType: 'immediate' };
      }

      // Verificar si tiene emails deshabilitados
      if (!prefs.email_enabled) {
        return { shouldSend: false };
      }

      // Verificar tipo específico de notificación
      const typeMapping: Record<string, string> = {
        'control_assignment': 'notify_control_assignment',
        'task_assigned': 'notify_task_assigned',
        'task_reminder': 'notify_task_reminder',
        'deadline_warning': 'notify_deadline_warning',
        'evidence_update': 'notify_evidence_update',
        'completion_request': 'notify_control_completed',
      };

      const prefKey = typeMapping[notificationType];
      if (prefKey && !prefs[prefKey]) {
        return { shouldSend: false };
      }

      // Determinar tipo de cola según frecuencia
      const queueType = prefs.email_frequency === 'immediate' ? 'immediate' :
                       prefs.email_frequency === 'daily' ? 'daily' :
                       prefs.email_frequency === 'weekly' ? 'weekly' :
                       'immediate';

      return { shouldSend: true, queueType };
    } catch (error) {
      logger.error('❌ [DATA] Error verificando preferencias de email:', error);
      // En caso de error, permitir envío por defecto
      return { shouldSend: true, queueType: 'immediate' };
    }
  }, []);

  /**
   * Encola un email para procesamiento asíncrono por el backend.
   *
   * El email no se envía directamente desde el frontend. En su lugar, se inserta un
   * registro en `email_queue` (o `email_digest_queue` si el usuario prefiere resumen)
   * que una función Edge o cron job procesará de forma asíncrona.
   *
   * Comportamiento según preferencias del usuario:
   * - `immediate`: inserta en `email_queue` → procesado en segundos
   * - `daily` / `weekly`: inserta en `email_digest_queue` → procesado en el próximo resumen
   * - `shouldSend = false`: omite silenciosamente sin error
   *
   * Nunca lanza excepciones hacia arriba — los errores de email no deben interrumpir
   * la operación principal (ej.: crear una tarea no debe fallar por un email fallido).
   *
   * @param notificationData - Datos del email (destinatario, asunto, cuerpo, tipo)
   * @param userId - UUID del usuario destinatario para verificar preferencias (opcional)
   */
  const queueEmail = useCallback(async (
    notificationData: Omit<EmailNotification, 'id' | 'sentAt' | 'status' | 'retryCount'>,
    userId?: string
  ) => {
    try {
      // Skip email en modo demo
      if (!user?.organization_id || user.organization_id.startsWith('demo-')) {
        logger.log('ℹ️ [DATA] Modo demo detectado, email no enviado');
        return { success: true, skipped: true, reason: 'demo_mode' };
      }

      // Validar campos requeridos
      if (!notificationData.to || !notificationData.subject || !notificationData.body) {
        throw new Error('Faltan campos requeridos para enviar email');
      }

      // Verificar preferencias si tenemos userId
      if (userId) {
        const { shouldSend, queueType } = await checkEmailPreferences(userId, notificationData.type);

        if (!shouldSend) {
          logger.log(`ℹ️ [DATA] Email omitido según preferencias de usuario ${userId}`);
          return { success: true, skipped: true, reason: 'user_preferences' };
        }

        // Si es digest, agregar a cola de digest en vez de email directo
        if (queueType === 'daily' || queueType === 'weekly') {
          logger.log(`📬 [DATA] Agregando email a digest ${queueType} para usuario ${userId}`);

          const { error: digestError } = await supabase
            .from('email_digest_queue')
            .insert({
              user_id: userId,
              organization_id: user.organization_id,
              notification_type: notificationData.type,
              notification_data: {
                to: notificationData.to,
                subject: notificationData.subject,
                body: notificationData.body,
                controlId: notificationData.controlId,
              },
              digest_type: queueType,
              scheduled_for: new Date().toISOString(),
            });

          if (digestError) {
            logger.error('❌ [DATA] Error agregando a digest queue:', digestError);
            throw digestError;
          }

          return { success: true, queued: true, queueType };
        }
      }

      logger.log(`📧 [DATA] Encolando email:`, notificationData);

      // Insertar en cola de emails para procesamiento asíncrono
      const { error: queueError } = await supabase
        .from('email_queue')
        .insert({
          organization_id: user.organization_id,
          email_data: {
            to: notificationData.to,
            subject: notificationData.subject,
            body: notificationData.body,
            type: notificationData.type,
            controlId: notificationData.controlId,
            userId: userId,
          },
          status: 'pending',
          scheduled_at: new Date().toISOString(),
          retry_count: 0,
          max_retries: 3,
        });

      if (queueError) {
        logger.error('❌ [DATA] Error encolando email:', queueError);
        throw queueError;
      }

      logger.log(`✅ [DATA] Email encolado exitosamente`);

      return { success: true, queued: true };
    } catch (error) {
      logger.error('❌ [DATA] Error en queueEmail:', error);

      // No lanzar error para no bloquear la operación principal
      logger.warn('⚠️ [DATA] Email no encolado pero operación continúa');
      return { success: false, error: error instanceof Error ? error.message : 'Error desconocido' };
    }
  }, [user?.organization_id, checkEmailPreferences]);

  /**
   * Wrapper de compatibilidad que delega a `queueEmail()`.
   * Mantiene la misma firma para no romper los llamadores existentes.
   */
  const sendEmailNotification = useCallback(async (
    notificationData: Omit<EmailNotification, 'id' | 'sentAt' | 'status' | 'retryCount'>,
    userId?: string
  ) => {
    return await queueEmail(notificationData, userId);
  }, [queueEmail]);

  // =====================================================
  // FUNCIONES DE TAREAS
  // =====================================================

  /**
   * Crea una tarea en un control ISO y actualiza el estado local.
   *
   * Flujo en modo real (con Supabase):
   * 1. Resolver `technical_referents.id` para el `assignee.id` (users.id), con caché.
   *    Si el admin no tiene registro en `technical_referents`, lo crea automáticamente.
   * 2. Insertar la tarea en la tabla `tasks`.
   * 3. Insertar items del checklist en `task_checklist_items` si los hay.
   * 4. Esperar 200ms para que los triggers de PostgreSQL procesen la auditoría base.
   * 5. Recargar el control completo con `loadSingleControl()` para obtener la tarea con su ID real.
   * 6. Agregar una entrada de audit trail enriquecida en memoria.
   * 7. Si `notify = true`, encolar email al asignado.
   *
   * Re-lanza el error hacia arriba para que el componente (DetailPanel) pueda
   * mostrar el mensaje de error al usuario.
   *
   * @param controlId - UUID del control al que agregar la tarea
   * @param taskData - Datos de la tarea sin los campos generados (id, createdAt, updatedAt)
   * @param notify - Si `true` (por defecto), encola email al referente asignado
   */
  const addTaskToControl = useCallback(async (
    controlId: string,
    taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>,
    notify = true
  ) => {
    try {
      logger.log(`📋 [DATA] Agregando tarea al control ${controlId}:`, taskData);

      if (user?.organization_id) {
        // Buscar el technical_referents.id que corresponde al users.id
        // Primero intentar desde el caché para evitar consultas redundantes
        let technicalReferentId = userToReferentIdCache.get(taskData.assignee.id);

        if (!technicalReferentId) {
          logger.log(`🔍 [DATA] Buscando technical_referent_id para user_id ${taskData.assignee.id} (caché miss)`);

          const { data: technicalReferentData, error: fetchError } = await supabase
            .from('technical_referents')
            .select('id')
            .eq('user_id', taskData.assignee.id)
            .maybeSingle();

          if (fetchError) {
            logger.error('❌ [DATA] Error de BD al buscar referente técnico:', fetchError);
            throw new Error(`Error de base de datos: ${fetchError.message}`);
          }

          if (!technicalReferentData) {
            // No existe fila en technical_referents (típico para admins) → crearla
            logger.log(`ℹ️ [DATA] Sin fila en technical_referents para ${taskData.assignee.id}, creando entrada...`);
            const { data: newRef, error: insertError } = await supabase
              .from('technical_referents')
              .insert({ user_id: taskData.assignee.id })
              .select('id')
              .single();

            if (insertError || !newRef?.id) {
              logger.error('❌ [DATA] No se pudo crear referente técnico para admin:', insertError);
              throw new Error(`No se encontró ni pudo crear el referente técnico para el usuario ${taskData.assignee.email ?? taskData.assignee.id}`);
            }

            technicalReferentId = newRef.id;
            logger.log(`✅ [DATA] Referente técnico creado automáticamente: ${technicalReferentId}`);
          } else {
            technicalReferentId = technicalReferentData.id;
          }

          // Guardar en caché para futuras consultas
          setUserToReferentIdCache(prev => new Map(prev).set(taskData.assignee.id, technicalReferentId!));
          logger.log(`✅ [DATA] ID de referente técnico guardado en caché: ${taskData.assignee.id} → ${technicalReferentId}`);
        } else {
          logger.log(`⚡ [DATA] ID de referente técnico obtenido del caché (caché hit): ${taskData.assignee.id} → ${technicalReferentId}`);
        }

        const technicalReferentData = { id: technicalReferentId };

        const { data, error, status: insertStatus } = await supabase
          .from('tasks')
          .insert([{
            control_id: controlId,
            title: taskData.title,
            description: taskData.description,
            status: taskData.status,
            priority: taskData.priority,
            assignee_id: technicalReferentData.id, // Usar technical_referents.id
            due_date: taskData.dueDate,
            estimated_hours: taskData.estimatedHours,
            actual_hours: taskData.actualHours
          }])
          .select()
          .single();

        // Validación completa de respuesta de BD
        if (error) {
          logger.error('❌ [DATA] Error de BD al insertar tarea:', error);
          throw new Error(`Error al crear la tarea: ${error.message}`);
        }

        if (!data) {
          logger.error('❌ [DATA] Respuesta vacía de BD al insertar tarea. Status:', insertStatus);
          throw new Error('La tarea no fue creada (respuesta vacía de la base de datos)');
        }

        if (!data.id) {
          logger.error('❌ [DATA] Tarea creada sin ID:', data);
          throw new Error('La tarea fue creada pero sin ID válido');
        }

        logger.log(`✅ [DATA] Tarea creada con ID: ${data.id}`);


        // Crear elementos del checklist si existen
        if (taskData.checklist && taskData.checklist.length > 0) {
          const checklistItems = taskData.checklist.map((item, index) => ({
            task_id: data.id,
            text: item.text,
            completed: item.completed,
            required: item.required,
            sort_order: index
          }));

          await supabase
            .from('task_checklist_items')
            .insert(checklistItems);
        }

        // Nota: No insertamos manualmente en audit_trail porque RLS lo bloquea
        // El trigger automático se encarga de crear la entrada base
        // Vamos a esperar un momento y luego recargar para obtener los datos del trigger
        await new Promise(resolve => setTimeout(resolve, 200));

        // Recargar solo el control modificado (mucho más eficiente)
        logger.log(`🔄 [DATA] Recargando control ${controlId} después de crear tarea...`);
        const updatedControl = await loadSingleControl(controlId);

        logger.log(`🔍 [DATA] Resultado de loadSingleControl:`, updatedControl ? 'Control cargado' : 'NULL');

        if (updatedControl) {
          // Validar que el control actualizado contiene la nueva tarea
          logger.log(`🔍 [DATA] Tareas en control recargado:`, updatedControl.tasks?.length || 0);
          logger.log(`🔍 [DATA] IDs de tareas:`, updatedControl.tasks?.map(t => t.id) || []);
          logger.log(`🔍 [DATA] Buscando tarea con ID:`, data.id);

          const taskExists = updatedControl.tasks?.some(t => t.id === data.id);
          if (!taskExists) {
            logger.error(`⚠️ [DATA] Control recargado pero no contiene la nueva tarea ${data.id}`);
            logger.error(`⚠️ [DATA] Tareas encontradas:`, updatedControl.tasks);
          } else {
            logger.log(`✅ [DATA] Tarea ${data.id} encontrada en el control recargado`);
          }

          // Crear entrada de audit trail enriquecida manualmente en memoria
          // (ya que RLS no permite insertar directamente en audit_trail)
          const enrichedAuditEntry: AuditEntry = {
            id: `audit_${Date.now()}`,
            action: `Creó tarea "${taskData.title}"`,
            actionType: 'task_created',
            user: {
              id: user.id,
              name: user.name,
              email: user.email,
              role: user.role,
              unit: user.unit || '',
              createdAt: new Date().toISOString(),
              isActive: true
            },
            timestamp: new Date().toISOString(),
            details: {
              taskId: data.id,
              taskTitle: taskData.title,
              priority: taskData.priority,
              dueDate: taskData.dueDate
            },
            previousValue: null,
            newValue: taskData.title,
            affectedUsers: [taskData.assignee],
            filesUploaded: [],
            impactMetrics: {}
          };

          // Agregar la entrada enriquecida al control recargado
          const controlWithEnrichedAudit = {
            ...updatedControl,
            auditTrail: [enrichedAuditEntry, ...updatedControl.auditTrail]
          };

          // Actualizar solo el control específico en el estado
          setControls(prev => prev.map(c =>
            c.id === controlId ? controlWithEnrichedAudit : c
          ));

          logger.log(`✅ [DATA] Control ${controlId} actualizado con nueva tarea y audit trail enriquecido`);

          // ✅ ENVIAR EMAIL DE NOTIFICACIÓN AL REFERENTE
          // Obtener información del control para el email
          const control = updatedControl;
          if (notify) try {
            await sendEmailNotification({
              to: taskData.assignee.email,
              subject: `Nueva tarea asignada: ${taskData.title}`,
              body: `
                <p><strong>Control:</strong> ${control.code} - ${control.title}</p>
                <p><strong>Tarea:</strong> ${taskData.title}</p>
                <p><strong>Descripción:</strong> ${taskData.description}</p>
                <p><strong>Prioridad:</strong> ${taskData.priority}</p>
                <p><strong>Fecha límite:</strong> ${new Date(taskData.dueDate).toLocaleDateString('es-ES')}</p>
              `,
              type: 'task_assigned',
              controlId: controlId
            }, taskData.assignee.id); // ✅ Pasar userId para verificar preferencias
            logger.log(`✅ [DATA] Email encolado para ${taskData.assignee.email}`);
          } catch (emailError) {
            logger.warn('⚠️ [DATA] Email no enviado pero tarea creada:', emailError);
            // No lanzar error, la tarea ya fue creada exitosamente
          }
        } else {
          logger.error(`❌ [DATA] No se pudo recargar el control ${controlId}`);
          throw new Error('Error al recargar el control después de crear la tarea');
        }
      } else {
        // Modo demo
        const newTask: Task = {
          ...taskData,
          id: `t${Date.now()}`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        const auditEntry = createAuditEntry(
          createTaskCreatedMessage(
            taskData.title,
            taskData.assignee,
            taskData.priority,
            taskData.dueDate
          ),
          'task_created',
          {
            affectedUsers: [taskData.assignee]
          }
        );

        // 🆕 FASE 1: Guardar en PostgreSQL
        await saveAuditEntryToDB(controlId, auditEntry);

        setControls(prev => prev.map(c =>
          c.id === controlId
            ? {
                ...c,
                tasks: [...(c.tasks || []), newTask],
                auditTrail: [...c.auditTrail, auditEntry]
              }
            : c
        ));
      }

      logger.log(`✅ [DATA] Tarea agregada al control ${controlId}`);

    } catch (err) {
      handleError(err, `agregar tarea al control ${controlId}`);
      throw err; // Re-lanzar para que DetailPanel pueda manejarlo
    }
  }, [handleError, user?.organization_id, loadSingleControl, createAuditEntry, sendEmailNotification, saveAuditEntryToDB]);

  /**
   * Actualiza campos de una tarea existente.
   * Si el cambio marca la tarea como `completed`, genera una entrada de audit trail
   * en el control padre y la persiste en PostgreSQL.
   * La actualización local se aplica a todos los controles que contengan la tarea
   * (aunque en la práctica cada tarea pertenece a un solo control).
   *
   * Manejo de errores: solo recarga desde BD si el error indica que la tarea no existe
   * (ocurriría antes de la actualización local). Para errores de BD posteriores, mantiene
   * el estado local ya actualizado para no perder cambios del usuario.
   */
  const updateTask = useCallback(async (taskId: string, updates: Partial<Task>) => {
    try {
      logger.log(`📝 [DATA] Actualizando tarea ${taskId}:`, updates);

      // Buscar la tarea antes de actualizar para obtener su información
      const task = controls
        .flatMap(c => c.tasks || [])
        .find(t => t.id === taskId);

      if (!task) {
        throw new Error(`Tarea ${taskId} no encontrada`);
      }

      // 🆕 Buscar el control al que pertenece la tarea
      const controlWithTask = controls.find(c =>
        (c.tasks || []).some(t => t.id === taskId)
      );

      if (!controlWithTask) {
        throw new Error(`Control no encontrado para tarea ${taskId}`);
      }

      // 🆕 Si se está completando la tarea, crear entrada de auditoría
      let auditEntry: AuditEntry | null = null;
      if (updates.status === 'completed' && task.status !== 'completed' && user) {
        auditEntry = createAuditEntry(
          createTaskCompletedMessage(task.title),
          'task_completed',
          {}
        );

        // Guardar en PostgreSQL
        await saveAuditEntryToDB(controlWithTask.id, auditEntry);

        logger.log(`✅ [AUDIT] Entrada de auditoría creada para tarea completada: ${task.title}`);
      }

      // Actualizar estado local (tasks + auditTrail si se creó auditoría)
      setControls(prev => prev.map(c => {
        if (c.id === controlWithTask.id) {
          return {
            ...c,
            tasks: (c.tasks || []).map(t =>
              t.id === taskId
                ? { ...t, ...updates, updatedAt: new Date().toISOString() }
                : t
            ),
            // Agregar entrada de auditoría al control si se creó
            auditTrail: auditEntry ? [...c.auditTrail, auditEntry] : c.auditTrail
          };
        }
        return {
          ...c,
          tasks: (c.tasks || []).map(t =>
            t.id === taskId
              ? { ...t, ...updates, updatedAt: new Date().toISOString() }
              : t
          )
        };
      }));

      if (user?.organization_id) {
        const { error } = await supabase
          .from('tasks')
          .update({
            status: updates.status,
            completed_at: updates.completedAt,
            actual_hours: updates.actualHours,
            updated_at: new Date().toISOString()
          })
          .eq('id', taskId);

        if (error) {
          throw error;
        }
      }

      logger.log(`✅ [DATA] Tarea ${taskId} actualizada`);

    } catch (err) {
      handleError(err, `actualizar tarea ${taskId}`);
      // ⚠️ Solo recargar si es un error crítico que requiere sincronización
      // No recargar si el error ocurrió después de actualizar el estado local
      // para evitar perder cambios de la sesión actual
      if (err instanceof Error && err.message.includes('no encontrada')) {
        // Error antes de actualizar estado local, recargar es seguro
        await loadControls();
      }
      // Para otros errores (ej: fallos en PostgreSQL), mantener el estado local
      // ya que la UI ya muestra el cambio y el usuario puede volver a intentar
    }
  }, [handleError, user?.organization_id, loadControls, controls, createAuditEntry, saveAuditEntryToDB]);

  /**
   * Elimina permanentemente una tarea. Aplica optimistic delete local antes de confirmar en BD.
   * Si la BD falla, recarga todos los controles para restaurar el estado correcto.
   */
  const deleteTask = useCallback(async (taskId: string) => {
    try {
      logger.log(`🗑️ [DATA] Eliminando tarea ${taskId}`);

      setControls(prev => prev.map(c => ({
        ...c,
        tasks: (c.tasks || []).filter(t => t.id !== taskId)
      })));

      if (user?.organization_id) {
        const { error } = await supabase
          .from('tasks')
          .delete()
          .eq('id', taskId);

        if (error) {
          throw error;
        }
      }

      logger.log(`✅ [DATA] Tarea ${taskId} eliminada`);

    } catch (err) {
      handleError(err, `eliminar tarea ${taskId}`);
      await loadControls();
    }
  }, [handleError, user?.organization_id, loadControls]);

  // =====================================================
  // FUNCIONES DE EVIDENCIAS
  // =====================================================

  /**
   * Sube un archivo de evidencia y registra los metadatos en la base de datos.
   *
   * Flujo de tres pasos:
   * 1. Subir el archivo a Supabase Storage (bucket configurado en `VITE_STORAGE_BUCKET_EVIDENCE`).
   *    La ruta es `{controlId}/{timestamp}-{random}.{ext}` para evitar colisiones.
   * 2. Insertar registro en la tabla `evidence` con la URL pública del archivo.
   * 3. Actualizar el estado local con la nueva evidencia y crear entrada de audit trail.
   *
   * Lanza error hacia arriba (sin silenciar) para que el componente pueda mostrar
   * el error al usuario y permitirle reintentar.
   *
   * @param controlId - UUID del control al que asociar la evidencia
   * @param file - Objeto File del navegador a subir
   * @param evidenceData - Nombre descriptivo y tipo de la evidencia
   */
  const addEvidenceToControl = useCallback(async (
    controlId: string,
    file: File,
    evidenceData: { name: string; type: 'document' | 'screenshot' | 'video' | 'other' }
  ) => {
    logger.log(`📎 [DATA] Subiendo evidencia al control ${controlId}:`, evidenceData, file);

    if (!user) {
      logger.error('❌ [DATA] Usuario no autenticado');
      throw new Error('Usuario no autenticado');
    }

    try {
      // 1. Subir archivo a Supabase Storage
      const bucket = import.meta.env.VITE_STORAGE_BUCKET_EVIDENCE || 'evidence-files';
      const fileExt = file.name.split('.').pop();
      const fileName = `${controlId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      logger.log(`📤 [DATA] Subiendo archivo a Storage: ${bucket}/${fileName}`);

      const { url: fileUrl, error: uploadError } = await uploadFile(bucket, fileName, file);

      if (uploadError || !fileUrl) {
        logger.error('❌ [DATA] Error subiendo archivo:', uploadError);
        throw new Error(uploadError?.message || 'Error al subir el archivo');
      }

      logger.log(`✅ [DATA] Archivo subido exitosamente: ${fileUrl}`);

      // 2. Crear registro en tabla evidence
      const evidenceRecord = {
        control_id: controlId,
        name: evidenceData.name,
        type: evidenceData.type,
        file_url: fileUrl,
        file_size: file.size,
        mime_type: file.type,
        status: 'pending' as const,
        uploaded_by: user.id,
        uploaded_at: new Date().toISOString(),
      };

      logger.log(`💾 [DATA] Creando registro de evidencia en BD`);

      const { data, error } = await supabase
        .from('evidence')
        .insert([evidenceRecord])
        .select('*')
        .single();

      if (error) {
        logger.error('❌ [DATA] Error creando registro de evidencia:', error);
        throw error;
      }

      logger.log(`✅ [DATA] Evidencia creada exitosamente:`, data);

      // 3. Actualizar estado local - agregar evidencia al control
      const newEvidence: Evidence = {
        id: data.id,
        name: data.name,
        type: data.type as 'document' | 'screenshot' | 'video' | 'other',
        url: data.file_url,
        size: data.file_size,
        status: data.status as 'pending' | 'approved' | 'rejected',
        uploadedBy: {
          id: user.id,
          name: user.name,
        },
        uploadedAt: data.uploaded_at,
      };

      // Crear audit trail enriquecido para subida de evidencia
      const auditEntry = createAuditEntry(
        createEvidenceUploadMessage(evidenceData.name, evidenceData.type, file.size),
        'evidence_upload',
        {
          filesUploaded: [{
            name: evidenceData.name,
            size: file.size
          }],
          impactMetrics: {
            completionChange: 5 // Subir evidencia suma progreso
          }
        }
      );

      // 🆕 FASE 1: Guardar en PostgreSQL
      await saveAuditEntryToDB(controlId, auditEntry);

      setControls(prev => prev.map(control =>
        control.id === controlId
          ? {
              ...control,
              evidence: [...(control.evidence || []), newEvidence],
              auditTrail: [...control.auditTrail, auditEntry]
            }
          : control
      ));

      logger.log(`✅ [DATA] Estado local actualizado con nueva evidencia`);
      return newEvidence;

    } catch (error) {
      logger.error('❌ [DATA] Error agregando evidencia:', error);
      throw error;
    }
  }, [createAuditEntry, saveAuditEntryToDB]);

  /**
   * Actualiza metadatos de una evidencia (nombre, estado de revisión).
   * Solo los campos `name` y `status` se persisten en BD (mapeo explícito defensivo).
   * Si el nuevo estado es `approved` o `rejected`, además:
   * - Crea una entrada de audit trail en el control padre.
   * - Envía un email al usuario que subió la evidencia (si `notify = true`).
   *
   * La actualización de BD se hace antes de la actualización local para garantizar
   * que si la BD rechaza el cambio no se muestra un estado incorrecto al usuario.
   */
  const updateEvidence = useCallback(async (evidenceId: string, updates: Partial<Evidence>, notify = true) => {
    try {
      logger.log(`📝 [DATA] Actualizando evidencia ${evidenceId}:`, updates);

      if (!user?.organization_id) {
        throw new Error('Usuario no autenticado o sin organización');
      }

      // Preparar datos para actualizar en BD
      const dbUpdates: Record<string, unknown> = {
        updated_at: new Date().toISOString()
      };

      // Mapear campos de interfaz Evidence a columnas de BD
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.status !== undefined) dbUpdates.status = updates.status;

      // Actualizar en base de datos
      const { error } = await supabase
        .from('evidence')
        .update(dbUpdates)
        .eq('id', evidenceId);

      if (error) {
        logger.error('❌ [DATA] Error actualizando evidencia:', error);
        throw error;
      }

      logger.log(`✅ [DATA] Evidencia actualizada en BD`);

      // Actualizar estado local
      setControls(prev => prev.map(control => ({
        ...control,
        evidence: (control.evidence || []).map(evidence =>
          evidence.id === evidenceId
            ? { ...evidence, ...updates }
            : evidence
        )
      })));

      // Crear audit trail y notificar si se aprobó/rechazó evidencia
      if (updates.status === 'approved' || updates.status === 'rejected') {
        const action = updates.status === 'approved'
          ? `Aprobó evidencia "${updates.name || 'sin nombre'}"`
          : `Rechazó evidencia "${updates.name || 'sin nombre'}"`;

        // Encontrar el control que contiene esta evidencia
        const controlWithEvidence = controls.find(c =>
          c.evidence?.some(e => e.id === evidenceId)
        );

        if (controlWithEvidence) {
          const auditEntry = createAuditEntry(
            action,
            'evidence_upload',
            {
              previousValue: 'pending',
              newValue: updates.status
            }
          );

          setControls(prev => prev.map(c =>
            c.id === controlWithEvidence.id
              ? { ...c, auditTrail: [...c.auditTrail, auditEntry] }
              : c
          ));

          // 🆕 Enviar notificación por email al usuario que subió la evidencia
          const evidence = controlWithEvidence.evidence?.find(e => e.id === evidenceId);
          if (notify && evidence && evidence.uploadedBy) {
            try {
              const statusText = updates.status === 'approved' ? 'aprobada' : 'rechazada';
              const statusIcon = updates.status === 'approved' ? '✅' : '❌';

              await sendEmailNotification({
                to: evidence.uploadedBy.email,
                subject: `${statusIcon} Evidencia ${statusText}: ${evidence.name}`,
                body: `
                  <p>Tu evidencia ha sido <strong>${statusText}</strong>:</p>
                  <p><strong>Control:</strong> ${controlWithEvidence.code} - ${controlWithEvidence.title}</p>
                  <p><strong>Evidencia:</strong> ${evidence.name}</p>
                  <p><strong>Estado:</strong> ${statusText.toUpperCase()}</p>
                  ${updates.status === 'rejected' ? '<p style="color: #dc2626;">Por favor, revisa y vuelve a subir la evidencia con las correcciones necesarias.</p>' : '<p style="color: #059669;">¡Excelente trabajo! La evidencia cumple con los requisitos.</p>'}
                `,
                type: 'evidence_update',
                controlId: controlWithEvidence.id
              }, evidence.uploadedBy.id);

              logger.log(`✅ [DATA] Email de evidencia ${statusText} encolado para ${evidence.uploadedBy.email}`);
            } catch (emailError) {
              logger.warn('⚠️ [DATA] Email no enviado pero evidencia actualizada:', emailError);
            }
          }
        }
      }

      logger.log(`✅ [DATA] Estado local actualizado`);

    } catch (error) {
      logger.error('❌ [DATA] Error en updateEvidence:', error);
      throw error;
    }
  }, [controls, createAuditEntry]);

  /**
   * Elimina una evidencia de la base de datos y opcionalmente de Supabase Storage.
   *
   * Flujo de cuatro pasos:
   * 1. Eliminar registro de la tabla `evidence`.
   * 2. Intentar eliminar el archivo de Storage extrayendo la ruta desde la URL.
   *    Si falla (ej.: archivo ya eliminado, permisos), solo loguea advertencia y continúa.
   * 3. Crear entrada de audit trail y persistirla en PostgreSQL.
   * 4. Actualizar el estado local eliminando la evidencia del control.
   *
   * Lanza error hacia arriba si la BD falla en el paso 1 (la operación principal).
   */
  const deleteEvidence = useCallback(async (evidenceId: string, controlId: string) => {
    try {
      logger.log(`🗑️ [DATA] Eliminando evidencia ${evidenceId} del control ${controlId}`);

      if (!user?.organization_id) {
        throw new Error('Usuario no autenticado o sin organización');
      }

      // Obtener información de la evidencia antes de eliminarla
      const control = controls.find(c => c.id === controlId);
      const evidence = control?.evidence?.find(e => e.id === evidenceId);

      if (!evidence) {
        throw new Error('Evidencia no encontrada');
      }

      // 1. Eliminar registro de base de datos
      const { error: dbError } = await supabase
        .from('evidence')
        .delete()
        .eq('id', evidenceId);

      if (dbError) {
        logger.error('❌ [DATA] Error eliminando evidencia de BD:', dbError);
        throw dbError;
      }

      logger.log(`✅ [DATA] Evidencia eliminada de BD`);

      // 2. Intentar eliminar archivo de Storage (no falla si hay error)
      try {
        // Extraer path del archivo de la URL
        const urlParts = evidence.url.split('/');
        const bucket = import.meta.env.VITE_STORAGE_BUCKET_EVIDENCE || 'evidence-files';
        const filePath = urlParts.slice(urlParts.indexOf(bucket) + 1).join('/');

        const { error: storageError } = await supabase.storage
          .from(bucket)
          .remove([filePath]);

        if (storageError) {
          logger.warn('⚠️ [DATA] No se pudo eliminar archivo del storage:', storageError);
          // No lanzar error, continuar con la eliminación
        } else {
          logger.log(`✅ [DATA] Archivo eliminado del storage`);
        }
      } catch (storageErr) {
        logger.warn('⚠️ [DATA] Error al intentar eliminar archivo del storage:', storageErr);
        // No lanzar error, continuar
      }

      // 3. Crear audit trail
      const auditEntry = createAuditEntry(
        createEvidenceDeleteMessage(evidence.name, evidence.type),
        'evidence_upload',
        {
          deletedEvidence: {
            name: evidence.name,
            type: evidence.type,
            size: evidence.size
          }
        }
      );

      // 🆕 FASE 1: Guardar en PostgreSQL
      await saveAuditEntryToDB(controlId, auditEntry);

      // 4. Actualizar estado local
      setControls(prev => prev.map(c =>
        c.id === controlId
          ? {
              ...c,
              evidence: c.evidence?.filter(e => e.id !== evidenceId) || [],
              auditTrail: [...c.auditTrail, auditEntry]
            }
          : c
      ));

      logger.log(`✅ [DATA] Evidencia eliminada exitosamente`);

    } catch (error) {
      logger.error('❌ [DATA] Error eliminando evidencia:', error);
      throw error;
    }
  }, [controls, createAuditEntry, saveAuditEntryToDB]);

  /**
   * Marca una notificación individual como leída con actualización optimista.
   * Registra `read_at` en la BD para auditoría de cuándo fue vista.
   */
  const markNotificationAsRead = useCallback(async (notificationId: string) => {
    try {
      logger.log(`✅ [DATA] Marcando notificación ${notificationId} como leída`);

      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId
            ? { ...notif, read: true }
            : notif
        )
      );

      if (user?.id) {
        const { error } = await supabase
          .from('notifications')
          .update({
            read: true,
            read_at: new Date().toISOString()
          })
          .eq('id', notificationId);

        if (error) {
          throw error;
        }
      }

    } catch (err) {
      handleError(err, `marcar notificación ${notificationId} como leída`);
    }
  }, [handleError, user?.id]);

  /**
   * Marca todas las notificaciones no leídas como leídas en una sola query (`IN` con IDs).
   * Aplica actualización optimista local primero; recarga desde BD si la query falla.
   */
  const markAllAsRead = useCallback(async () => {
    try {
      logger.log('✅ [DATA] Marcando todas las notificaciones como leídas');

      const unreadIds = notifications
        .filter(n => !n.read)
        .map(n => n.id);

      if (unreadIds.length === 0) {
        logger.log('ℹ️ [DATA] No hay notificaciones sin leer');
        return;
      }

      // Actualización optimista local primero
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));

      // Actualizar en Supabase con una sola query
      if (user?.id && !user.id.startsWith('demo-')) {
        const { error } = await supabase
          .from('notifications')
          .update({
            read: true,
            read_at: new Date().toISOString()
          })
          .in('id', unreadIds);

        if (error) {
          throw error;
        }
      }

      logger.log(`✅ [DATA] ${unreadIds.length} notificaciones marcadas como leídas`);

    } catch (err) {
      handleError(err, 'marcar todas las notificaciones como leídas');
      // Recargar en caso de error
      await loadNotifications();
    }
  }, [notifications, user?.id, handleError, loadNotifications]);

  /**
   * Elimina permanentemente una notificación. Optimistic delete local primero.
   * No se ejecuta la query en modo demo (IDs con prefijo `demo-`).
   */
  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      logger.log(`🗑️ [DATA] Eliminando notificación ${notificationId}`);

      // Actualización optimista - eliminar localmente primero
      setNotifications(prev => prev.filter(n => n.id !== notificationId));

      // Eliminar de la base de datos si no es modo demo
      if (user?.id && !user.id.startsWith('demo-')) {
        const { error } = await supabase
          .from('notifications')
          .delete()
          .eq('id', notificationId);

        if (error) {
          throw error;
        }
      }

      logger.log(`✅ [DATA] Notificación ${notificationId} eliminada`);

    } catch (err) {
      handleError(err, `eliminar notificación ${notificationId}`);
      // Recargar notificaciones en caso de error
      await loadNotifications();
    }
  }, [user?.id, handleError, loadNotifications]);

  /**
   * Elimina todas las notificaciones ya leídas en una sola query (`IN` con IDs leídos).
   * Optimistic delete local antes de confirmar en BD.
   */
  const deleteAllRead = useCallback(async () => {
    try {
      logger.log('🗑️ [DATA] Eliminando todas las notificaciones leídas');

      const readIds = notifications
        .filter(n => n.read)
        .map(n => n.id);

      if (readIds.length === 0) {
        logger.log('ℹ️ [DATA] No hay notificaciones leídas para eliminar');
        return;
      }

      // Actualización optimista - eliminar localmente primero
      setNotifications(prev => prev.filter(n => !n.read));

      // Eliminar de la base de datos si no es modo demo
      if (user?.id && !user.id.startsWith('demo-')) {
        const { error } = await supabase
          .from('notifications')
          .delete()
          .in('id', readIds);

        if (error) {
          throw error;
        }
      }

      logger.log(`✅ [DATA] ${readIds.length} notificaciones leídas eliminadas`);

    } catch (err) {
      handleError(err, 'eliminar notificaciones leídas');
      // Recargar notificaciones en caso de error
      await loadNotifications();
    }
  }, [notifications, user?.id, handleError, loadNotifications]);

  /**
   * Crea una notificación in-app en la tabla `notifications`.
   * Si la notificación es para el usuario actual, también la agrega al estado local
   * para que aparezca inmediatamente en el centro de notificaciones (sin esperar Realtime).
   * Si la notificación es para otro usuario, se inserta en BD y ese usuario la recibirá
   * vía la suscripción Realtime cuando se conecte.
   * En modo demo, solo agrega al estado local con ID temporal.
   */
  const createNotification = useCallback(async (data: {
    userId: string;
    title: string;
    message: string;
    type: 'info' | 'warning' | 'error' | 'success';
    actionUrl?: string;
    actionText?: string;
  }) => {
    try {
      logger.log('🔔 [DATA] Creando notificación:', data);

      if (!user?.organization_id) {
        logger.warn('⚠️ [DATA] No se puede crear notificación sin organización');
        return;
      }

      // En modo demo, solo agregar localmente
      if (user.id.startsWith('demo-')) {
        const newNotification: Notification = {
          id: `notif-${Date.now()}`,
          title: data.title,
          message: data.message,
          type: data.type,
          timestamp: new Date().toISOString(),
          read: false,
          actionUrl: data.actionUrl,
          actionText: data.actionText
        };

        setNotifications(prev => [newNotification, ...prev]);
        logger.log('✅ [DATA] Notificación creada en modo demo');
        return;
      }

      // Crear en Supabase
      const { data: insertedNotification, error } = await supabase
        .from('notifications')
        .insert([{
          organization_id: user.organization_id,
          user_id: data.userId,
          title: data.title,
          message: data.message,
          type: data.type,
          action_url: data.actionUrl,
          action_text: data.actionText,
          read: false
        }])
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Si la notificación es para el usuario actual, agregarla al estado local
      if (data.userId === user.id && insertedNotification) {
        const newNotification: Notification = {
          id: insertedNotification.id,
          title: insertedNotification.title,
          message: insertedNotification.message,
          type: insertedNotification.type,
          timestamp: insertedNotification.created_at,
          read: insertedNotification.read,
          actionUrl: insertedNotification.action_url,
          actionText: insertedNotification.action_text
        };

        setNotifications(prev => [newNotification, ...prev]);
      }

      logger.log('✅ [DATA] Notificación creada exitosamente');

    } catch (err) {
      handleError(err, 'crear notificación');
    }
  }, [user, handleError]);

  // =====================================================
  // DATOS DERIVADOS
  // =====================================================

  /**
   * Subconjunto de `controls` que pasa todos los criterios activos.
   * Recalculado solo cuando cambia `controls`, `searchQuery` o `filter` (memoizado).
   *
   * Lógica de filtrado (todas las condiciones deben cumplirse, AND):
   * - `searchQuery`: coincidencia en título, código, descripción, categoría, subcategoría o nombre de referente
   * - `filter.status`: array vacío = sin restricción; de lo contrario el control debe estar en el array
   * - `filter.priority`: igual que status
   * - `filter.category`: igual que status
   * - `filter.assignee`: el control debe tener al menos un referente cuyo nombre esté en el array
   * - `filter.dateRange`: ambos extremos son opcionales; se compara con `dueDate` del control
   */
  const filteredControls = React.useMemo(() => {
    return controls.filter(control => {
      // Filtro de búsqueda
      const matchesSearch = searchQuery === '' || 
        control.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        control.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        control.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        control.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        control.subcategory.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (control.assignedReferents || []).some(referent => 
          referent.name.toLowerCase().includes(searchQuery.toLowerCase())
        );

      // Filtros específicos
      const matchesStatus = filter.status.length === 0 || filter.status.includes(control.status);
      const matchesPriority = filter.priority.length === 0 || filter.priority.includes(control.priority);
      const matchesCategory = filter.category.length === 0 || filter.category.includes(control.category);
      const matchesAssignee = filter.assignee.length === 0 || 
        (control.assignedReferents || []).some(referent => filter.assignee.includes(referent.name));
      
      // Filtro de fechas
      const matchesDateRange = 
        (filter.dateRange.start === '' || new Date(control.dueDate) >= new Date(filter.dateRange.start)) &&
        (filter.dateRange.end === '' || new Date(control.dueDate) <= new Date(filter.dateRange.end));

      return matchesSearch && matchesStatus && matchesPriority && matchesCategory && matchesAssignee && matchesDateRange;
    });
  }, [controls, searchQuery, filter]);

  // =====================================================
  // EFECTOS
  // =====================================================
  
  /**
   * Efecto principal de carga de datos. Se ejecuta cuando el usuario autentica o cambia.
   *
   * Espera a que `dbUserReady = true` antes de cargar para garantizar que
   * `user.organization_id` es el valor de PostgreSQL (no solo el JWT local).
   * Esto previene cargar datos de la organización incorrecta durante el arranque.
   *
   * Usa `Promise.allSettled()` en lugar de `Promise.all()` para que si una carga
   * falla (ej.: tabla de notificaciones no disponible), las otras continúen normalmente.
   *
   * El timeout global de 60 segundos previene que `isLoading` quede en `true` indefinidamente
   * si alguna consulta cuelga sin respuesta.
   *
   * El flag `isMounted` previene actualizaciones de estado después de que el componente
   * se desmonte (evita el warning "Can't perform a state update on an unmounted component").
   */
  useEffect(() => {
    let isMounted = true;
    let dataTimeout: NodeJS.Timeout | null = null;

    const loadData = async () => {
      if (!isMounted) return;

      try {
        if (!user) {
          logger.log('🚪 [DATA] Usuario no autenticado, usando datos demo...');
          if (isMounted) {
            setControls(MOCK_CONTROLS);
            setTechnicalReferents(MOCK_REFERENTS);
            setNotifications(MOCK_NOTIFICATIONS);
            setEmailNotifications([]);
            setIsLoading(false);
          }
          return;
        }

        if (!dbUserReady) {
          logger.log('⏳ [DATA] Esperando sync con BD antes de cargar datos...');
          return;
        }

        logger.log('👤 [DATA] Usuario autenticado, cargando datos...');
        setIsLoading(true);

        // Timeout de seguridad para DataContext - aumentado a 60s
        dataTimeout = setTimeout(() => {
          logger.warn('⚠️ [DATA] Timeout de carga de datos alcanzado (60s) - forzando fin de carga');
          if (isMounted) {
            setIsLoading(false);
          }
        }, 60000); // 60 segundos timeout global

        logger.log('🔄 [DATA] Iniciando carga paralela de datos...');

        // Usar Promise.allSettled para que siempre termine incluso si una query falla
        const results = await Promise.allSettled([
          loadControls(),
          loadTechnicalReferents(),
          loadNotifications()
        ]);

        // Log de resultados
        results.forEach((result, index) => {
          const names = ['controles', 'referentes', 'notificaciones'];
          if (result.status === 'rejected') {
            logger.warn(`⚠️ [DATA] Error cargando ${names[index]}:`, result.reason);
          } else {
            logger.log(`✅ [DATA] ${names[index]} cargados correctamente`);
          }
        });

        if (dataTimeout) clearTimeout(dataTimeout);

        logger.log('✅ [DATA] Proceso de carga completado');

        if (isMounted) {
          setIsLoading(false);
        }

      } catch (err) {
        logger.error('❌ [DATA] Error cargando datos:', err);
        if (dataTimeout) clearTimeout(dataTimeout);
        if (isMounted) {
          handleError(err, 'cargar datos');
          setIsLoading(false);
        }
      }
    };

    loadData();

    return () => {
      logger.log('🧹 [DATA] Cleanup de efecto de carga de datos');
      isMounted = false;
      if (dataTimeout) clearTimeout(dataTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.organization_id, dbUserReady]); // ✅ Solo depende de IDs y sync BD

  /**
   * Efecto de diagnóstico: loguea el estado actual cada vez que cambia algún contador.
   * Útil para depurar problemas de carga o sincronización durante el desarrollo.
   */
  useEffect(() => {
    logger.log('📊 [DATA] Estado actual:', {
      controls: controls.length,
      referents: technicalReferents.length,
      notifications: notifications.length,
      filteredControls: filteredControls.length,
      isLoading,
      hasError: !!error,
      userConnected: !!user
    });
  }, [controls.length, technicalReferents.length, notifications.length, filteredControls.length, isLoading, error, user]);

  /**
   * Suscripción Supabase Realtime para notificaciones del usuario actual.
   * Filtra por `user_id=eq.{user.id}` para recibir solo las notificaciones propias.
   * Agrega las nuevas notificaciones al inicio del array (más recientes primero).
   * Se desuscribe al desmontar para evitar fugas de memoria.
   */
  useEffect(() => {
    // No suscribirse si no hay usuario o es modo demo
    if (!user?.id || user.id.startsWith('demo-')) {
      return;
    }

    logger.log('🔔 [DATA] Configurando suscripción en tiempo real para notificaciones...');

    // Crear canal de Supabase Realtime
    const channel = supabase
      .channel('notifications-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          logger.log('🔔 [DATA] Nueva notificación recibida en tiempo real:', payload);

          // Transformar la notificación al formato de la app
          const newNotification: Notification = {
            id: payload.new.id,
            title: payload.new.title,
            message: payload.new.message,
            type: payload.new.type,
            timestamp: payload.new.created_at,
            read: payload.new.read,
            actionUrl: payload.new.action_url,
            actionText: payload.new.action_text
          };

          // Agregar al inicio de la lista
          setNotifications(prev => [newNotification, ...prev]);

          logger.log('✅ [DATA] Notificación agregada al estado local');
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          logger.log('✅ [DATA] Suscripción a notificaciones en tiempo real activa');
        } else if (status === 'CHANNEL_ERROR') {
          logger.error('❌ [DATA] Error en canal de notificaciones en tiempo real');
        } else if (status === 'TIMED_OUT') {
          logger.warn('⚠️ [DATA] Timeout en suscripción de notificaciones');
        }
      });

    // Cleanup al desmontar
    return () => {
      logger.log('🧹 [DATA] Cerrando suscripción de notificaciones en tiempo real');
      channel.unsubscribe();
    };
  }, [user?.id]);

  /**
   * Suscripciones Supabase Realtime para las entidades principales del sistema.
   * Configura 5 canales: controles, tareas, asignaciones, evidencias y audit_trail.
   *
   * Estrategia por tipo de evento:
   * - `UPDATE` en controles: recarga el control individual con `loadSingleControl()` para
   *   preservar el audit trail (hacer un merge parcial podría perder entradas).
   * - `INSERT/DELETE` en controles: recarga toda la lista (cambio estructural).
   * - `UPDATE` en tareas: actualiza la tarea en el estado local directamente (más eficiente).
   * - `INSERT/DELETE` en tareas: recarga toda la lista (necesita actualizar el control padre).
   * - Asignaciones, evidencias, audit_trail: recarga toda la lista en cualquier evento.
   *
   * Las dependencias del efecto son solo `user.id` y `user.organization_id` (IDs estables).
   * `loadControls` y `loadSingleControl` se usan en los callbacks pero NO están en las
   * dependencias para evitar re-suscripciones por cada re-render. Los closures acceden
   * a la versión más reciente de las funciones gracias al comportamiento de JavaScript.
   */
  useEffect(() => {
    // No suscribirse si no hay usuario o es modo demo
    if (!user?.organization_id || user.id?.startsWith('demo-')) {
      return;
    }

    logger.log('📊 [DATA] Configurando suscripciones en tiempo real para controles y tareas...');

    // Canal para controles ISO
    const controlsChannel = supabase
      .channel('controls-realtime')
      .on(
        'postgres_changes',
        {
          event: '*', // Escuchar INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'iso_controls',
          filter: `organization_id=eq.${user.organization_id}`
        },
        (payload) => {
          logger.log('📊 [DATA] Cambio en controles detectado:', payload.eventType);

          if (payload.eventType === 'UPDATE') {
            // 🆕 FIX: Recargar control completo con auditTrail desde la DB
            // Esto previene que se pierda el historial de auditoría
            logger.log('🔄 [DATA] Recargando control completo con auditTrail:', payload.new.id);
            loadSingleControl(payload.new.id).then(updatedControl => {
              if (updatedControl) {
                setControls(prev => prev.map(c =>
                  c.id === updatedControl.id ? updatedControl : c
                ));
                logger.log('✅ [DATA] Control actualizado en tiempo real con auditTrail completo');
              }
            }).catch(err => {
              logger.error('❌ [DATA] Error recargando control:', err);
            });
          } else if (payload.eventType === 'INSERT' || payload.eventType === 'DELETE') {
            // Para INSERT/DELETE, recargar toda la lista
            // Usar la versión más reciente de loadControls del closure
            logger.log('🔄 [DATA] Recargando controles por', payload.eventType);
            loadControls();
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          logger.log('✅ [DATA] Suscripción a controles en tiempo real activa');
        } else if (status === 'CHANNEL_ERROR') {
          logger.error('❌ [DATA] Error en canal de controles');
        }
      });

    // Canal para tareas
    const tasksChannel = supabase
      .channel('tasks-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tasks'
        },
        (payload) => {
          logger.log('📋 [DATA] Cambio en tareas detectado:', payload.eventType);
          // ⚠️ NO recargar todo - esto borra el auditTrail local
          // En su lugar, solo actualizar la tarea específica si es necesario
          if (payload.eventType === 'UPDATE' && payload.new) {
            setControls(prev => prev.map(control => ({
              ...control,
              tasks: (control.tasks || []).map(task =>
                task.id === payload.new.id
                  ? { ...task, ...payload.new, updatedAt: payload.new.updated_at }
                  : task
              )
            })));
            logger.log('✅ [DATA] Tarea actualizada en tiempo real:', payload.new.id);
          }
          // Para INSERT/DELETE de tareas, sí necesitamos recargar
          else if (payload.eventType === 'INSERT' || payload.eventType === 'DELETE') {
            logger.log('🔄 [DATA] Recargando controles por', payload.eventType, 'de tarea');
            loadControls();
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          logger.log('✅ [DATA] Suscripción a tareas en tiempo real activa');
        }
      });

    // Canal para asignaciones de controles
    const assignmentsChannel = supabase
      .channel('assignments-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'control_assignments'
        },
        (payload) => {
          logger.log('👥 [DATA] Cambio en asignaciones detectado:', payload.eventType);
          // Recargar controles para actualizar asignaciones
          loadControls();
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          logger.log('✅ [DATA] Suscripción a asignaciones en tiempo real activa');
        }
      });

    // Canal para evidencias
    const evidenceChannel = supabase
      .channel('evidence-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'evidence'
        },
        (payload) => {
          logger.log('📎 [DATA] Cambio en evidencias detectado:', payload.eventType);
          // Recargar controles para actualizar evidencias
          loadControls();
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          logger.log('✅ [DATA] Suscripción a evidencias en tiempo real activa');
        }
      });

    // 🆕 Canal para audit_trail - actualizar auditorías en tiempo real
    const auditChannel = supabase
      .channel('audit-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'audit_trail',
          filter: `organization_id=eq.${user.organization_id}`
        },
        (payload) => {
          logger.log('📋 [DATA] Nueva entrada de auditoría detectada:', payload.new);

          // Agregar entrada de auditoría al control correspondiente
          if (payload.new && payload.new.table_name === 'iso_controls') {
            const newAudit: AuditEntry = {
              id: payload.new.id,
              timestamp: payload.new.created_at || new Date().toISOString(),
              user: payload.new.user_name ? {
                id: payload.new.user_id,
                name: payload.new.user_name,
                email: payload.new.user_email || '',
                role: 'technical_referent',
                createdAt: '',
                isActive: true
              } : {
                id: 'unknown',
                name: 'Usuario',
                email: '',
                role: 'technical_referent',
                createdAt: '',
                isActive: true
              },
              action: payload.new.action || payload.new.description || 'Acción desconocida',
              actionType: ((payload.new.metadata as Record<string, unknown>)?.actionType as string) || 'other',
              metadata: payload.new.metadata || {}
            };

            setControls(prev => prev.map(control =>
              control.id === payload.new.record_id
                ? {
                    ...control,
                    auditTrail: control.auditTrail.some(e => e.id === newAudit.id)
                      ? control.auditTrail // ya existe por actualización optimista, no duplicar
                      : [...control.auditTrail, newAudit]
                  }
                : control
            ));

            logger.log('✅ [DATA] Auditoría agregada al control:', payload.new.record_id);
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          logger.log('✅ [DATA] Suscripción a auditoría en tiempo real activa');
        }
      });

    // Cleanup al desmontar
    return () => {
      logger.log('🧹 [DATA] Cerrando suscripciones en tiempo real');
      controlsChannel.unsubscribe();
      tasksChannel.unsubscribe();
      assignmentsChannel.unsubscribe();
      evidenceChannel.unsubscribe();
      auditChannel.unsubscribe();
    };
    // ✅ CORRECCIÓN: Solo depende de IDs estables, no de loadControls
    // loadControls se usa dentro pero JavaScript closure garantiza versión actual
  }, [user?.id, user?.organization_id]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Polling de respaldo cada 5 minutos para sincronizar datos cuando las suscripciones
   * Realtime fallen o pierdan conexión (desconexión de red, timeout de WebSocket, etc.).
   *
   * `refreshData` se llama dentro del callback del intervalo pero NO está en las dependencias.
   * Esto es intencional: `refreshData` es estable (useCallback con deps estables) y agregar
   * funciones a las dependencias de un efecto con `setInterval` causaría que el intervalo
   * se reinicie cada vez que se re-cree la función, resultando en un loop infinito.
   * El closure siempre referencia la versión más reciente de `refreshData`.
   */
  useEffect(() => {
    // No hacer polling si no hay usuario o es modo demo
    if (!user?.id || user.id.startsWith('demo-')) {
      return;
    }

    logger.log('⏰ [DATA] Configurando polling de respaldo (5 minutos)...');

    const POLLING_INTERVAL = 5 * 60 * 1000; // 5 minutos

    const interval = setInterval(() => {
      logger.log('🔄 [DATA] Ejecutando polling de respaldo - refrescando datos...');
      // refreshData siempre será la versión más reciente gracias al closure
      refreshData();
    }, POLLING_INTERVAL);

    return () => {
      logger.log('🧹 [DATA] Limpiando polling de respaldo');
      clearInterval(interval);
    };
    // ✅ CORRECCIÓN CRÍTICA: Solo depende de user?.id
    // refreshData se ejecuta pero NO debe estar en dependencias para evitar loop infinito
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // =====================================================
  // VALOR DEL CONTEXTO
  // =====================================================
  
  const contextValue: DataContextType = {
    // Estados principales
    controls,
    notifications,
    technicalReferents,
    emailNotifications,

    // Estados de UI
    filter,
    searchQuery,
    isLoading,
    error,
    isDemoMode, // ✅ NUEVO: Exponer estado de modo demo

    // Funciones de UI
    setFilter,
    setSearchQuery,
    clearError,
    refreshData,
    
    // Datos derivados
    filteredControls,
    
    // Funciones de controles
    updateControl,
    assignControlToReferent,
    loadSingleControl,
    
    // Funciones de referentes
    addTechnicalReferent,
    updateTechnicalReferent,
    deleteTechnicalReferent,
    enableAdminAsReferent,
    disableAdminAsReferent,
    
    // Funciones de tareas
    addTaskToControl,
    updateTask,
    deleteTask,
    
    // Funciones de evidencias
    addEvidenceToControl,
    updateEvidence,
    deleteEvidence,

    // Funciones de notificaciones
    createNotification,
    sendEmailNotification,
    markNotificationAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllRead
  };

  return (
    <DataContext.Provider value={contextValue}>
      {children}
    </DataContext.Provider>
  );
}

// =====================================================
// HOOKS PERSONALIZADOS
// =====================================================

/**
 * Hook de acceso al contexto de datos ISO 27001/27002.
 * Lanza un error descriptivo si se usa fuera de `DataProvider`.
 * @returns Toda la API del contexto (estados + funciones CRUD)
 */
export function useData() {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData debe ser usado dentro de un DataProvider');
  }
  return context;
}

/**
 * Hook para obtener métricas calculadas en tiempo real sobre los controles filtrados.
 *
 * Calcula métricas de tres dimensiones:
 * - **Controles**: totales, por estado, por prioridad crítica, tasa de completación
 * - **Tareas**: totales, por estado, vencidas (fecha pasada y no completadas), tasa de completación
 * - **Referentes**: total y activos
 *
 * Usa `useMemo` anidado dentro de `useData()` para que las métricas se recalculen
 * solo cuando `filteredControls` o `technicalReferents` cambien, no en cada render.
 *
 * @returns Objeto con métricas agrupadas por categoría
 */
export function useMetrics() {
  const { filteredControls, technicalReferents } = useData();

  return React.useMemo(() => {
    const totalControls = filteredControls.length;
    const completedControls = filteredControls.filter(c => c.status === 'completed').length;
    const inProgressControls = filteredControls.filter(c => c.status === 'in_progress').length;
    const criticalControls = filteredControls.filter(c => c.priority === 'critical').length;

    // Aplanar todas las tareas de todos los controles filtrados en un solo array
    const allTasks = filteredControls.flatMap(c => c.tasks || []);
    const completedTasks = allTasks.filter(t => t.status === 'completed').length;
    const inProgressTasks = allTasks.filter(t => t.status === 'in_progress').length;
    const pendingTasks = allTasks.filter(t => t.status === 'pending').length;
    // Vencida = no completada y con fecha de vencimiento en el pasado
    const overdueTasks = allTasks.filter(t =>
      t.status !== 'completed' && new Date(t.dueDate) < new Date()
    ).length;

    const activeReferents = technicalReferents.filter(r => r.isActive).length;

    // Tasas expresadas como porcentaje (0–100), protegidas contra división por cero
    const completionRate = totalControls > 0 ? (completedControls / totalControls) * 100 : 0;
    const taskCompletionRate = allTasks.length > 0 ? (completedTasks / allTasks.length) * 100 : 0;

    const metrics = {
      controls: {
        total: totalControls,
        completed: completedControls,
        inProgress: inProgressControls,
        critical: criticalControls,
        completionRate
      },
      tasks: {
        total: allTasks.length,
        completed: completedTasks,
        inProgress: inProgressTasks,
        pending: pendingTasks,
        overdue: overdueTasks,
        completionRate: taskCompletionRate
      },
      referents: {
        total: technicalReferents.length,
        active: activeReferents
      }
    };

    return metrics;
  }, [filteredControls, technicalReferents]);
}